import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { usePersistedState } from "../utils/usePersistedState";
import { formatAmount, formatNumber } from "../utils/format";
import {
  Box,
  Grid,
  Typography,
  Card,
  Chip,
  TextField,

  
  InputAdornment,
  MenuItem,
  Select,
  IconButton,
  Button,
  Tooltip,
  Avatar,
  GlobalStyles,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Popover,
  LinearProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CancelIcon from "@mui/icons-material/Cancel";
import CancelledReceivedDialog, { useCancelledReceived } from "../components/CancelledReceivedDialog";
import PeopleIcon from "@mui/icons-material/People";
import PaymentsIcon from "@mui/icons-material/Payments";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DescriptionIcon from "@mui/icons-material/Description";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ClearIcon from "@mui/icons-material/Clear";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import TuneIcon from "@mui/icons-material/Tune";
import StorefrontIcon from "@mui/icons-material/Storefront";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckIcon from "@mui/icons-material/Check";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import Layout from "../layout/Layout";
import UAESalesMap from "../components/UAESalesMap";
import moment from "moment";
import { apiData } from "../services/api_service";
import { salesExecDashboardAPI, listStoreAPI, fetchCustomerDDAPI } from "../config";
import Chart from "react-apexcharts";
import SalesTargetWidget from "./SalesTargets/SalesTargetWidget";

// Modern Shiny Shimmer Skeleton Component
const ShinySkeleton = ({ width, height, borderRadius = 6, style = {} }) => (
  <Box
    sx={{
      width: width || "100%",
      height: height || 20,
      borderRadius: typeof borderRadius === "number" ? `${borderRadius}px` : borderRadius,
      background: "linear-gradient(90deg, #e2e8f0 0%, #f8fafc 35%, #ffffff 50%, #f8fafc 65%, #e2e8f0 100%)",
      backgroundSize: "400% 100%",
      animation: "shinyShimmer 1.4s cubic-bezier(0.4, 0, 0.2, 1) infinite",
      boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
      "@keyframes shinyShimmer": {
        "0%": { backgroundPosition: "100% 0%" },
        "100%": { backgroundPosition: "0% 0%" },
      },
      ...style,
    }}
  />
);

// ─── Count-up animation hook ────────────────────────────────────────────────
// Animates a number from 0 → target when `active` becomes true.
// Automatically disabled when the user prefers reduced motion.
const useCountUp = (target, duration = 750, active = false) => {
  const [count, setCount] = useState(0);
  const rafRef = useRef(null);
  useEffect(() => {
    const numTarget = parseFloat(target) || 0;
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!active || prefersReduced) {
      setCount(numTarget);
      return;
    }
    setCount(0);
    const startTime = performance.now();
    const tick = (now) => {
      const p = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 4); // easeOutQuart
      setCount(Math.round(eased * numTarget));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, active, duration]);
  return count;
};
// ────────────────────────────────────────────────────────────────────────────

function SalesExecDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = usePersistedState("dashboard:fromDate", "");
  const [toDate, setToDate] = usePersistedState("dashboard:toDate", "");
  const [storeId, setStoreId] = usePersistedState("dashboard:storeId", 0);
  const [customerId, setCustomerId] = usePersistedState("dashboard:customerId", 0);
  const [customersList, setCustomersList] = useState([]);
  const [salesPersonId, setSalesPersonId] = usePersistedState("dashboard:salesPersonId", 0);
  const [salesPersonsList, setSalesPersonsList] = useState([]);
  const [period, setPeriod] = usePersistedState("dashboard:period", "monthly");
  const [stores, setStores] = useState([]);
  const [searchQuery, setSearchQuery] = usePersistedState("dashboard:search", "");
  // While a search is active, only the cards the search actually filters are shown (invoice-based ones).
  const searching = String(searchQuery || "").trim() !== "";
  const [productSort, setProductSort] = useState("top");
  const [customerSort, setCustomerSort] = useState("top");
  const [trendPeriod, setTrendPeriod] = useState("monthly");
  const [productsPeriod, setProductsPeriod] = useState("monthly");
  const [execPerfPeriod, setExecPerfPeriod] = useState("monthly");
  const [showDamageTable, setShowDamageTable] = useState(false);

  // ── Animation state ──────────────────────────────────────────
  const [mounted, setMounted] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);
  // ─────────────────────────────────────────────────────────────

  // ── Floating Quick Filter State ──────────────────────────────
  const [scrolledDown, setScrolledDown] = useState(false);
  const [filterExpanded, setFilterExpanded] = useState(false);

  useEffect(() => {
    const handleScroll = (e) => {
      const target = e?.target;
      if (
        target &&
        typeof target.className === "string" &&
        (target.className.includes("MuiList") ||
          target.className.includes("MuiAutocomplete") ||
          target.className.includes("MuiMenu") ||
          target.className.includes("MuiPaper") ||
          target.className.includes("MuiPopover"))
      ) {
        return;
      }

      const pageScrollTop =
        window.scrollY ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        (target && target.classList && target.classList.contains("MuiBox-root") ? target.scrollTop : 0);

      if (pageScrollTop > 150) {
        setScrolledDown(true);
      } else {
        setScrolledDown(false);
        setFilterExpanded(false);
      }
    };
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, []);
  // ─────────────────────────────────────────────────────────────

  const [dashboardData, setDashboardData] = useState({
    summary: {},
    itemSales: [],
    recentOrders: [],
    salesTrend: [],
    topCustomers: [],
    inventoryAlerts: {},
    salesDistribution: [],
    categorySales: [],
    regionalSales: [],
    channelSales: [],
    salesPersons: [],
    damagedReport: {},
  });

  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const allowedMenus = userData?.menus
    ? String(userData.menus).split(",").map((id) => parseInt(id.trim(), 10))
    : [];

  const isAdmin =
    Number(userData?.role_id || userData?.position_id || userData?.position) === 377 ||
    Number(userData?.role_id) === 1 ||
    String(userData?.role_name || userData?.role || "").toLowerCase().includes("admin") ||
    String(userData?.position_name || userData?.position || "").toLowerCase().includes("admin") ||
    userData?.user_type === "admin";

  const hasPermission = (menuId) => {
    if (!userData?.menus) return false;
    return allowedMenus.includes(Number(menuId));
  };

  const [trendLoading, setTrendLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);

  const fetchStoresList = async () => {
    try {
      const res = await apiData("get", listStoreAPI);
      if (res?.success) {
        setStores(res.data || []);
      }
    } catch (e) {
      console.error("Failed to load stores:", e);
    }
  };

  const fetchCustomersDropdown = async () => {
    try {
      const res = await apiData("get", fetchCustomerDDAPI);
      const list = res?.data?.result || (Array.isArray(res?.data) ? res.data : []);
      if (Array.isArray(list)) {
        setCustomersList(list);
      }
    } catch (e) {
      console.error("Failed to load customers dropdown:", e);
    }
  };

  // Only the most recent dashboard request may update the screen: otherwise a
  // slower earlier request (e.g. the initial This Month load) lands after the
  // newly chosen filter (All Time) and resets the cards, and its completion
  // hides the loader while the latest request is still running.
  const dashboardFetchSeq = useRef(0);
  const searchEffectMounted = useRef(false);

  const fetchDashboardData = async () => {
    const seq = ++dashboardFetchSeq.current;
    setLoading(true);
    setTrendLoading(true);
    setProductsLoading(true);
    setCustomersLoading(true);
    setExecPerfLoading(true);
    try {
      const payload = {
        user_id: userData?.emp_id || userData?.id || userData?.user_id,
        position_id: userData?.position_id || userData?.position || userData?.role_id || userData?.user_type,
        position_name: userData?.position_name || userData?.role_name,
        role_id: userData?.role_id || userData?.position_id || userData?.position,
        user_type: userData?.user_type,
        menus: userData?.menus,
        store_id: storeId,
        customer_id: customerId,
        sales_person_id: salesPersonId,
        period: period,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        search: searchQuery || undefined,
        product_sort: productSort === "bottom" ? "ASC" : "DESC",
        customer_sort: customerSort === "bottom" ? "ASC" : "DESC",
        trend_period: trendPeriod,
        products_period: productsPeriod,
      };
      const res = await apiData("get", salesExecDashboardAPI, payload);
      if (seq !== dashboardFetchSeq.current) return; // superseded by a newer request
      if (res?.success && res.data) {
        setDashboardData(res.data);
        setExecPerfSummary(res.data.summary || null);
        if (Array.isArray(res.data.salesPersons) && res.data.salesPersons.length > 0) {
          setSalesPersonsList(res.data.salesPersons);
        }
      }
    } catch (err) {
      console.error("Failed to fetch Sales Exec dashboard:", err);
    } finally {
      if (seq === dashboardFetchSeq.current) {
        setLoading(false);
        setTrendLoading(false);
        setProductsLoading(false);
        setCustomersLoading(false);
        setExecPerfLoading(false);
      }
    }
  };

  const handleTrendPeriodChange = (newPeriod) => {
    setTrendPeriod(newPeriod);
    fetchTrendData(newPeriod);
  };

  const fetchTrendData = async (specificPeriod) => {
    setTrendLoading(true);
    try {
      const payload = {
        user_id: userData?.emp_id || userData?.id || userData?.user_id,
        position_id: userData?.position_id || userData?.position || userData?.role_id || userData?.user_type,
        role_id: userData?.role_id || userData?.position_id || userData?.position,
        position_name: userData?.position_name || userData?.role_name,
        menus: userData?.menus,
        store_id: storeId,
        customer_id: customerId,
        sales_person_id: salesPersonId,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        trend_period: specificPeriod,
      };
      const res = await apiData("get", salesExecDashboardAPI, payload);
      if (res?.success && res.data?.salesTrend) {
        setDashboardData((prev) => ({ ...prev, salesTrend: res.data.salesTrend }));
      }
    } catch (err) {
      console.error("Failed to fetch Sales Trend:", err);
    } finally {
      setTrendLoading(false);
    }
  };

  const handleProductsPeriodChange = (newPeriod) => {
    setProductsPeriod(newPeriod);
    fetchProductsData(newPeriod, productSort);
  };

  const handleProductSortChange = (newSort) => {
    setProductSort(newSort);
    fetchProductsData(productsPeriod, newSort);
  };

  const fetchProductsData = async (specificPeriod, sortDir) => {
    setProductsLoading(true);
    try {
      const payload = {
        user_id: userData?.emp_id || userData?.id || userData?.user_id,
        position_id: userData?.position_id || userData?.position || userData?.role_id || userData?.user_type,
        role_id: userData?.role_id || userData?.position_id || userData?.position,
        position_name: userData?.position_name || userData?.role_name,
        menus: userData?.menus,
        store_id: storeId,
        customer_id: customerId,
        sales_person_id: salesPersonId,
        products_period: specificPeriod,
        product_sort: sortDir === "bottom" ? "ASC" : "DESC",
      };
      const res = await apiData("get", salesExecDashboardAPI, payload);
      if (res?.success && res.data?.itemSales) {
        setDashboardData((prev) => ({ ...prev, itemSales: res.data.itemSales }));
      }
    } catch (err) {
      console.error("Failed to fetch Products Data:", err);
    } finally {
      setProductsLoading(false);
    }
  };

  const handleCustomerSortChange = (newSort) => {
    setCustomerSort(newSort);
    fetchCustomersData(newSort);
  };

  const fetchCustomersData = async (sortDir) => {
    setCustomersLoading(true);
    try {
      const payload = {
        user_id: userData?.emp_id || userData?.id || userData?.user_id,
        position_id: userData?.position_id || userData?.position || userData?.role_id || userData?.user_type,
        role_id: userData?.role_id || userData?.position_id || userData?.position,
        position_name: userData?.position_name || userData?.role_name,
        menus: userData?.menus,
        store_id: storeId,
        customer_id: customerId,
        sales_person_id: salesPersonId,
        customer_sort: sortDir === "bottom" ? "ASC" : "DESC",
      };
      const res = await apiData("get", salesExecDashboardAPI, payload);
      if (res?.success && res.data?.topCustomers) {
        setDashboardData((prev) => ({ ...prev, topCustomers: res.data.topCustomers }));
      }
    } catch (err) {
      console.error("Failed to fetch Customers Data:", err);
    } finally {
      setCustomersLoading(false);
    }
  };

  const [execPerfLoading, setExecPerfLoading] = useState(false);
  const [execPerfSummary, setExecPerfSummary] = useState(null);

  const [damagedPeriod, setDamagedPeriod] = useState("monthly");
  const [damageSort, setDamageSort] = useState("high");
  const [damagedLoading, setDamagedLoading] = useState(false);
  const [damagedReportData, setDamagedReportData] = useState(null);

  const handleExecPerfPeriodChange = (newPeriod) => {
    setExecPerfPeriod(newPeriod);
    fetchExecPerfData(newPeriod);
  };

  const fetchExecPerfData = async (specificPeriod) => {
    setExecPerfLoading(true);
    try {
      const payload = {
        user_id: userData?.emp_id || userData?.id || userData?.user_id,
        position_id: userData?.position_id || userData?.position || userData?.role_id || userData?.user_type,
        role_id: userData?.role_id || userData?.position_id || userData?.position,
        position_name: userData?.position_name || userData?.role_name,
        menus: userData?.menus,
        store_id: storeId,
        customer_id: customerId,
        sales_person_id: salesPersonId,
        period: specificPeriod,
      };
      const res = await apiData("get", salesExecDashboardAPI, payload);
      if (res?.success && res.data?.summary) {
        setExecPerfSummary(res.data.summary);
      }
    } catch (err) {
      console.error("Failed to fetch Executive Performance:", err);
    } finally {
      setExecPerfLoading(false);
    }
  };

  const handleDamagedPeriodChange = (newPeriod) => {
    setDamagedPeriod(newPeriod);
    fetchDamagedData(newPeriod);
  };

  const fetchDamagedData = async (specificPeriod) => {
    setDamagedLoading(true);
    setDamagedReportData(null);
    try {
      const todayStr = moment().format("YYYY-MM-DD");
      let dFrom = "";
      let dTo = "";
      if (specificPeriod === "daily") {
        dFrom = todayStr; dTo = todayStr;
      } else if (specificPeriod === "weekly") {
        dFrom = moment().subtract(7, "days").format("YYYY-MM-DD"); dTo = todayStr;
      } else if (specificPeriod === "monthly") {
        dFrom = moment().subtract(1, "months").format("YYYY-MM-DD"); dTo = todayStr;
      } else if (specificPeriod === "yearly") {
        dFrom = moment().startOf("year").format("YYYY-MM-DD"); dTo = todayStr;
      }

      const payload = {
        user_id: userData?.emp_id || userData?.id || userData?.user_id,
        role_id: userData?.role_id || userData?.position_id || userData?.position,
        store_id: storeId,
        customer_id: customerId,
        sales_person_id: salesPersonId,
        period: specificPeriod,
        ...(dFrom && { from_date: dFrom }),
        ...(dTo && { to_date: dTo }),
      };
      const res = await apiData("get", salesExecDashboardAPI, payload);
      if (res?.success && res.data?.damagedReport) {
        setDamagedReportData(res.data.damagedReport);
        setDashboardData((prev) => ({ ...prev, damagedReport: res.data.damagedReport }));
      }
    } catch (err) {
      console.error("Failed to fetch Damaged Report:", err);
    } finally {
      setDamagedLoading(false);
    }
  };

  useEffect(() => {
    fetchStoresList();
    fetchCustomersDropdown();
    // The initial dashboard load is done by the filters effect below.
  }, []);

  useEffect(() => {
    setExecPerfSummary(null);
    setDamagedReportData(null);
    fetchDashboardData();
  }, [storeId, customerId, salesPersonId, period, fromDate, toDate]);

  // Solution 4: Debounced auto-search when user types
  useEffect(() => {
    if (!searchEffectMounted.current) {
      searchEffectMounted.current = true; // initial load is handled by the filters effect
      return undefined;
    }
    const timer = setTimeout(() => {
      fetchDashboardData();
    }, 400);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    setTrendPeriod(newPeriod);
    setProductsPeriod(newPeriod);
    setExecPerfPeriod(newPeriod);
    setDamagedPeriod(newPeriod);
    setExecPerfSummary(null);
    setDamagedReportData(null);

    const today = moment().format("YYYY-MM-DD");
    if (newPeriod === "all") {
      setFromDate("");
      setToDate("");
    } else if (newPeriod === "daily") {
      setFromDate(today);
      setToDate(today);
    } else if (newPeriod === "weekly") {
      setFromDate(moment().subtract(7, "days").format("YYYY-MM-DD"));
      setToDate(today);
    } else if (newPeriod === "monthly") {
      setFromDate(moment().subtract(1, "months").format("YYYY-MM-DD"));
      setToDate(today);
    } else if (newPeriod === "yearly") {
      setFromDate(moment().startOf("year").format("YYYY-MM-DD"));
      setToDate(today);
    }
  };

  const handleSearchSubmit = (e) => {
    if (e.key === "Enter") {
      fetchDashboardData();
    }
  };

  const formatCurrency = (amt) => {
    return `AED ${formatAmount(amt)}`;
  };

  const getProductImageUrl = (imgData) => {
    if (!imgData) return null;
    let url = imgData;
    if (typeof imgData === "string") {
      const trimmed = imgData.trim();
      if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed) && parsed.length > 0) {
            url = parsed[0];
          }
        } catch (e) {
          url = trimmed.replace(/^\["|"]$/g, "").replace(/\\"/g, "");
        }
      }
    }
    return url && typeof url === "string" && url.trim() !== "" ? url.trim() : null;
  };

  const summary = dashboardData.summary || {};
  // A search is active, the data has loaded, and no invoice matched it.
  const searchNoMatch =
    searching && !loading && Number(summary?.period_orders ?? summary?.monthly_orders ?? 0) === 0;
  const todaySales = summary.daily_sales || 0;
  const todayPaymentBreakdown = Array.isArray(summary.payment_mode_breakdown_today)
    ? summary.payment_mode_breakdown_today
    : [];
  const totalPaymentBreakdown = Array.isArray(summary.payment_mode_breakdown_total)
    ? summary.payment_mode_breakdown_total
    : [];
  const monthlySales = summary.monthly_sales || 0;
  const ordersProcessed = summary.monthly_orders || 0;
  const newCustomers = summary.new_customers || 0;
  const collectionsReceived = summary.collections_received || 0;
  const pendingCollections = summary.pending_collections || 0;
  const aov = summary.aov || 0;
  const focTotal = summary.foc_total || 0;
  const focPending = summary.foc_pending || 0;
  const focConverted = summary.foc_converted || 0;
  const focRejected = summary.foc_rejected || 0;

  const damagedReport = damagedReportData || dashboardData.damagedReport || {};
  const damagedSummary = damagedReport.summary || { total_damaged_qty: 0, total_financial_loss: 0, total_incidents: 0 };
  const damagedTopProducts = Array.isArray(damagedReport.topProducts) ? damagedReport.topProducts : [];
  const damagedStoreBreakdown = Array.isArray(damagedReport.storeBreakdown) ? damagedReport.storeBreakdown : [];
  const damagedRecords = Array.isArray(damagedReport.records) ? damagedReport.records : [];
  const isDamagedLoading = loading || damagedLoading;

  const sortedDamagedRecords = useMemo(() => {
    const list = [...damagedRecords];
    list.sort((a, b) => {
      const lossA = Number(a.total_loss || 0);
      const lossB = Number(b.total_loss || 0);
      return damageSort === "high" ? lossB - lossA : lossA - lossB;
    });
    return list.slice(0, 10);
  }, [damagedRecords, damageSort]);

  const getGrowthText = (val, label = "vs Last Month") => {
    const rawNum = parseFloat(val || 0);
    if (rawNum > 0) {
      const displayNum = Math.min(100, Math.round(rawNum));
      const suffix = rawNum > 100 ? "+" : "";
      return { change: `↑ ${displayNum}%${suffix} ${label}`, isUp: true };
    }
    if (rawNum < 0) {
      const displayNum = Math.max(-100, Math.round(rawNum));
      const suffix = rawNum < -100 ? "+" : "";
      return { change: `↓ ${Math.abs(displayNum)}%${suffix} ${label}`, isUp: false };
    }
    return { change: `0% ${label}`, isUp: true };
  };

  const compLabel = summary.comparison_label || "vs Prev Month";
  const todayGrowth = getGrowthText(summary.today_sales_growth, "vs Yesterday");
  const monthlyGrowth = getGrowthText(summary.monthly_sales_growth, compLabel);
  const ordersGrowth = getGrowthText(summary.orders_growth, compLabel);
  const newCustomersGrowth = getGrowthText(summary.new_customers_growth, compLabel);
  const collectionsGrowth = getGrowthText(summary.collections_growth, compLabel);
  const pendingGrowth = getGrowthText(summary.pending_growth, compLabel);
  const aovGrowth = getGrowthText(summary.aov_growth, compLabel);

  const salesCardTitle = (fromDate || toDate)
    ? "Total Sales"
    : period === "yearly"
      ? "Yearly Sales"
      : period === "weekly"
        ? "Weekly Sales"
        : period === "daily"
          ? "Today's Sales"
          : period === "all"
            ? "Total Sales"
            : "Monthly Sales";
  const pendingGrnCount = summary.pending_grn_count || 0;
  const cdnDocCount = summary.cdn_count || 0;
  const cdnPaymentBreakdown = Array.isArray(summary.cdn_payment_breakdown)
    ? summary.cdn_payment_breakdown
    : [];
  const proformaDocCount = summary.proforma_quote_count || 0;
  const proformaPaymentBreakdown = Array.isArray(summary.proforma_payment_breakdown)
    ? summary.proforma_payment_breakdown
    : [];

  // Count-up values for the three purely-numeric KPI cards
  const countOrders = useCountUp(ordersProcessed, 750, mounted && !loading);
  const countCustomers = useCountUp(newCustomers, 750, mounted && !loading);

  const getCardFilterState = (cardType) => {
    let effectiveFrom = fromDate;
    let effectiveTo = toDate;
    const todayStr = moment().format("YYYY-MM-DD");

    if (!effectiveFrom && !effectiveTo) {
      if (period === "daily") {
        effectiveFrom = todayStr;
        effectiveTo = todayStr;
      } else if (period === "weekly") {
        effectiveFrom = moment().subtract(7, "days").format("YYYY-MM-DD");
        effectiveTo = todayStr;
      } else if (period === "monthly") {
        // Must match the backend's own "monthly" window (calendar month
        // start, not a rolling 30 days — see monthStartStr in
        // salesExecDashboardModel.js) so a card's drill-through total lines
        // up with the figure shown on the card itself.
        effectiveFrom = moment().startOf("month").format("YYYY-MM-DD");
        effectiveTo = todayStr;
      } else if (period === "yearly") {
        effectiveFrom = moment().startOf("year").format("YYYY-MM-DD");
        effectiveTo = todayStr;
      }
    }

    return {
      fromDate: effectiveFrom,
      toDate: effectiveTo,
      storeId,
      customerId,
      salesPersonId,
      period,
      cardType,
    };
  };

  // Money already received on invoices / CDNs / Proformas that were cancelled
  // (nothing is deleted — they stay on record). The section only shows when
  // there is a value, and follows the same period / store / customer filters.
  const [cancelledTypeOpen, setCancelledTypeOpen] = useState(null); // null | "all" | "invoice" | "cdn" | "proforma"
  const cancelledFilters = (() => {
    const f = getCardFilterState("cdn");
    return {
      from_date: f.fromDate || "",
      to_date: f.toDate || "",
      store_id: Number(storeId) > 0 ? storeId : "",
      customer_id: Number(customerId) > 0 ? customerId : "",
      created_by: Number(salesPersonId) > 0 ? salesPersonId : "",
    };
  })();
  const cancelledRecv = useCancelledReceived(undefined, cancelledFilters);

  // Each cancelled card opens the matching list, filtered to cancelled documents that
  // still hold money (same period / store / customer filters as the card). Only the
  // combined "Total" card has no single list, so it keeps the summary popup.
  const openCancelledCard = (key) => {
    const f = getCardFilterState("cdn");
    const common = {
      fromDate: f.fromDate,
      toDate: f.toDate,
      storeId: f.storeId,
      customerId: f.customerId,
      createdBy: summary.doc_created_by,
    };
    if (key === "invoice") {
      navigate("/order/list", {
        state: { ...getCardFilterState("cancelled_invoice"), paymentStatus: "cancelled", reportTitle: "Cancelled Invoices — Payment Received" },
      });
    } else if (key === "cdn") {
      navigate("/proforma-invoice/list", {
        state: { ...common, statusScope: "cancelled", advanceFilter: "any_advance", reportTitle: "Cancelled CDNs — Payment Received" },
      });
    } else if (key === "proforma") {
      navigate("/proforma-quote/list", {
        state: { ...common, statusScope: "cancelled", advanceFilter: "any_advance", reportTitle: "Cancelled Proformas — Payment Received" },
      });
    } else {
      setCancelledTypeOpen(key);
    }
  };

  // Consistent color per payment method so the same method always reads the
  // same color across cards (Cash, Card, Cheque, Bank Transfer, ...).
  const PAYMENT_METHOD_COLORS = {
    cash: "#16a34a",
    card: "#6366f1",
    cheque: "#f59e0b",
    "bank transfer": "#0ea5e9",
    credit: "#dc2626",
    "unpaid / credit": "#dc2626",
    unspecified: "#94a3b8",
  };
  const paymentMethodColorPalette = ["#6366f1", "#16a34a", "#f59e0b", "#0ea5e9", "#dc2626", "#9333ea", "#0891b2"];
  const getPaymentMethodColor = (method, idx) =>
    PAYMENT_METHOD_COLORS[String(method || "").trim().toLowerCase()] ||
    paymentMethodColorPalette[idx % paymentMethodColorPalette.length];

  // Compact "payment mix" widget: a single segmented bar showing each
  // method's share of the total, plus a color-coded legend underneath.
  // Replaces a row of loose chips with something that reads at a glance.
  // `asCount` shows each method's number of documents (row.orders) instead of
  // its currency amount — used by the CDN / Proforma cards.
  const renderPaymentSplitBar = (breakdown, path, baseFilterState, asCount = false) => {
    const valueOf = (row) => parseFloat(asCount ? row.orders : row.amount) || 0;
    const displayOf = (row) => (asCount ? String(valueOf(row)) : formatCurrency(row.amount));
    const total = breakdown.reduce((sum, row) => sum + valueOf(row), 0);
    if (total <= 0) return null;

    // Clicking a method (bar segment or legend row) drills into Orders
    // pre-filtered to just that payment method; stopPropagation keeps it
    // from also triggering the card's own "view all" click underneath it.
    const goToMethod = (e, method) => {
      e.stopPropagation();
      if (!path) return;
      if (!asCount && method === "Unpaid / Credit") {
        // Unpaid balance (credit invoices) — not a payment method.
        navigate(path, { state: { ...baseFilterState, paymentStatus: "pending" } });
      } else if (!asCount) {
        // Method rows count what was RECEIVED through that method.
        navigate(path, { state: { ...baseFilterState, paymentMethod: method, paymentStatus: "received" } });
      } else {
        navigate(path, { state: { ...baseFilterState, paymentMethod: method } });
      }
    };

    return (
      <Box sx={{ mt: 1, pt: 1, borderTop: "1px dashed #e2e8f0" }}>
        <Box
          sx={{
            display: "flex",
            width: "100%",
            height: 6,
            borderRadius: 3,
            overflow: "hidden",
            backgroundColor: "#f1f5f9",
          }}
        >
          {breakdown.map((row, i) => {
            const pct = (valueOf(row) / total) * 100;
            return (
              <Tooltip key={i} title={`${row.method}: ${displayOf(row)} (${pct.toFixed(1)}%) — click to view`} arrow placement="top">
                <Box
                  onClick={(e) => goToMethod(e, row.method)}
                  sx={{
                    width: `${pct}%`,
                    minWidth: pct > 0 ? 2 : 0,
                    backgroundColor: getPaymentMethodColor(row.method, i),
                    transition: "width 0.3s ease, opacity 0.15s ease",
                    cursor: path ? "pointer" : "default",
                    "&:hover": { opacity: 0.75 },
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.75 }}>
          {breakdown.map((row, i) => (
            <Box
              key={i}
              onClick={(e) => goToMethod(e, row.method)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.4,
                cursor: path ? "pointer" : "default",
                borderRadius: 1,
                px: 0.3,
                "&:hover": { backgroundColor: path ? "#f1f5f9" : "transparent" },
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: getPaymentMethodColor(row.method, i),
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: "0.6rem", fontWeight: 600, color: "#64748b" }}>
                {row.method}
              </Typography>
              <Typography sx={{ fontSize: "0.62rem", fontWeight: 800, color: "#0f172a" }}>
                {displayOf(row)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  // Segmented bar + legend for a value-based split (CDN / Proforma Pending
  // Amount: to collect / advance received / settled). Each row carries its own
  // colour, count, amount and the list filter it drills into.
  const renderValueSplitBar = (breakdown, path, baseFilterState, emptyText = "Nothing open") => {
    const total = breakdown.reduce((sum, row) => sum + (parseFloat(row.value) || 0), 0);
    if (total <= 0) {
      return (
        <Typography sx={{ mt: 1, pt: 1, borderTop: "1px dashed #e2e8f0", fontSize: "0.66rem", color: "#94a3b8", fontWeight: 600 }}>
          {emptyText}
        </Typography>
      );
    }
    // Rows with nothing in them (0 documents, AED 0) are left out of the legend.
    const rows = breakdown.filter((row) => (parseFloat(row.value) || 0) > 0 || (row.count || 0) > 0);
    const goTo = (e, row) => {
      e.stopPropagation();
      if (!path) return;
      navigate(row.path || path, { state: { ...baseFilterState, ...row.filterState } });
    };
    return (
      <Box sx={{ mt: 1, pt: 1, borderTop: "1px dashed #e2e8f0" }}>
        <Box sx={{ display: "flex", width: "100%", height: 6, borderRadius: 3, overflow: "hidden", backgroundColor: "#f1f5f9" }}>
          {breakdown.map((row, i) => {
            const pct = ((parseFloat(row.value) || 0) / total) * 100;
            if (pct <= 0) return null;
            return (
              <Tooltip key={i} title={`${row.tooltip || row.label}: ${formatCurrency(row.value)} (${pct.toFixed(1)}%) — click to view`} arrow placement="top">
                <Box
                  onClick={(e) => goTo(e, row)}
                  sx={{ width: `${pct}%`, minWidth: 3, backgroundColor: row.color, transition: "width 0.3s ease, opacity 0.15s ease", cursor: path ? "pointer" : "default", "&:hover": { opacity: 0.75 } }}
                />
              </Tooltip>
            );
          })}
        </Box>
        <Box sx={{ display: "grid", gap: 0.4, mt: 0.9 }}>
          {rows.map((row, i) => (
            <Box
              key={i}
              onClick={(e) => goTo(e, row)}
              sx={{ display: "flex", alignItems: "center", gap: 0.6, cursor: path ? "pointer" : "default", borderRadius: 1, px: 0.3, "&:hover": { backgroundColor: path ? "#f1f5f9" : "transparent" } }}
            >
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: row.color, flexShrink: 0 }} />
              <Typography sx={{ fontSize: "0.66rem", fontWeight: 600, color: "#64748b", flex: 1 }}>
                {row.label}{row.count !== undefined ? ` · ${formatNumber(row.count)}` : ""}
              </Typography>
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, color: "#0f172a", whiteSpace: "nowrap" }}>
                {formatCurrency(row.value)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  // Same "segmented bar + legend" widget as renderPaymentSplitBar, but for a
  // plain status breakdown (counts, not currency) — e.g. FOC Requests'
  // Pending / Converted / Rejected split. Each row already carries its own
  // color and the statusTab to drill into, so no color-lookup helper needed.
  const renderStatusSplitBar = (breakdown, path, baseFilterState) => {
    const total = breakdown.reduce((sum, row) => sum + (parseFloat(row.count) || 0), 0);
    if (total <= 0) return null;

    const goToStatus = (e, statusTab) => {
      e.stopPropagation();
      if (!path) return;
      navigate(path, { state: { ...baseFilterState, statusTab } });
    };

    return (
      <Box sx={{ mt: 1, pt: 1, borderTop: "1px dashed #e2e8f0" }}>
        <Box
          sx={{
            display: "flex",
            width: "100%",
            height: 6,
            borderRadius: 3,
            overflow: "hidden",
            backgroundColor: "#f1f5f9",
          }}
        >
          {breakdown.map((row, i) => {
            const pct = (parseFloat(row.count) / total) * 100;
            if (pct <= 0) return null;
            return (
              <Tooltip key={i} title={`${row.label}: ${formatNumber(row.count)} (${pct.toFixed(1)}%) — click to view`} arrow placement="top">
                <Box
                  onClick={(e) => goToStatus(e, row.statusTab)}
                  sx={{
                    width: `${pct}%`,
                    minWidth: 2,
                    backgroundColor: row.color,
                    transition: "width 0.3s ease, opacity 0.15s ease",
                    cursor: path ? "pointer" : "default",
                    "&:hover": { opacity: 0.75 },
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.75 }}>
          {breakdown.map((row, i) => (
            <Box
              key={i}
              onClick={(e) => goToStatus(e, row.statusTab)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.4,
                cursor: path ? "pointer" : "default",
                borderRadius: 1,
                px: 0.3,
                "&:hover": { backgroundColor: path ? "#f1f5f9" : "transparent" },
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: row.color,
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: "0.6rem", fontWeight: 600, color: "#64748b" }}>
                {row.label}
              </Typography>
              <Typography sx={{ fontSize: "0.62rem", fontWeight: 800, color: "#0f172a" }}>
                {formatNumber(row.count)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const statCards = [
    { title: salesCardTitle, value: formatCurrency(monthlySales), change: monthlyGrowth.change, isUp: monthlyGrowth.isUp, icon: <ShoppingCartIcon />, color: "#16a34a", bg: "#f0fdf4", path: "/order/list", filterState: { ...getCardFilterState("monthly_sales"), reportTitle: `${salesCardTitle} Report` }, note: `${summary.period_orders || 0} ${Number(summary.period_orders) === 1 ? "invoice" : "invoices"}`, paymentBreakdown: totalPaymentBreakdown },
    { title: "Collections Received", value: formatCurrency(collectionsReceived), change: collectionsGrowth.change, isUp: collectionsGrowth.isUp, icon: <PaymentsIcon />, color: "#15803d", bg: "#f0fdf4", path: "/order/list", filterState: { ...getCardFilterState("collections_received"), paymentStatus: "received", reportTitle: "Collections Received Report" },
      emptyText: "No collections in this period",
      note: `${summary.period_orders || 0} invoices · ${formatCurrency(summary.period_sales || 0)} total`,
      valueBreakdown: [
        { label: "Fully paid invoices", count: summary.received_full_count || 0, value: summary.received_full_amount || 0, color: "#16a34a", filterState: { paymentStatus: "fullypaid", reportTitle: "Fully Paid Invoices" } },
        { label: "Part-paid credit invoices", count: summary.partial_count || 0, value: summary.received_partial_amount || 0, color: "#f59e0b", filterState: { paymentStatus: "partial", reportTitle: "Partially Paid Credit Invoices" } },
        { label: "Unpaid invoices", count: summary.unpaid_count || 0, value: 0, color: "#ea580c", filterState: { paymentStatus: "unpaid", reportTitle: "Unpaid Invoices" } },
        { label: "Zero-value / FOC invoices", count: summary.zero_value_count || 0, value: 0, color: "#94a3b8", filterState: { paymentStatus: "zero", reportTitle: "Zero-Value Invoices" } },
      ] },
    { title: "Pending Collections", subtitle: "from Credit Invoices (balance due)", value: formatCurrency(pendingCollections), change: pendingGrowth.change, isUp: pendingGrowth.isUp, icon: <ReceiptLongIcon />, color: "#ea580c", bg: "#fff7ed", path: "/order/list", filterState: { ...getCardFilterState("pending_collections"), paymentStatus: "pending", reportTitle: "Pending Collections Report" },
      emptyText: "No pending balance",
      note: `${summary.period_orders || 0} invoices · ${formatCurrency(summary.period_sales || 0)} total`,
      valueBreakdown: [
        { label: "Fully paid invoices", count: summary.received_full_count || 0, value: 0, color: "#16a34a", filterState: { paymentStatus: "fullypaid", reportTitle: "Fully Paid Invoices" } },
        { label: "Part-paid credit invoices", count: summary.partial_count || 0, value: summary.pending_partial_amount || 0, color: "#f59e0b", filterState: { paymentStatus: "partial", reportTitle: "Partially Paid Credit Invoices" } },
        { label: "Unpaid invoices", count: summary.unpaid_count || 0, value: summary.pending_unpaid_amount || 0, color: "#ea580c", filterState: { paymentStatus: "unpaid", reportTitle: "Unpaid Invoices" } },
        { label: "Zero-value / FOC invoices", count: summary.zero_value_count || 0, value: 0, color: "#94a3b8", filterState: { paymentStatus: "zero", reportTitle: "Zero-Value Invoices" } },
      ] },
    { title: "Proforma Total", note: `Value of Proformas not yet invoiced · ${proformaDocCount} Proformas in total`, value: formatCurrency((summary.proforma_pending_total_value || 0) + (summary.proforma_pending_settled_value || 0)), change: `${summary.proforma_pending_doc_count || 0} Pending · ${summary.proforma_converted_count || 0} Converted to Invoice`, statusLinks: [{ label: `${summary.proforma_pending_doc_count || 0} Pending`, filterState: { statusScope: "open", reportTitle: "Proformas — Pending (not yet invoiced)" } }, { label: `${summary.proforma_converted_count || 0} Converted to Invoice`, filterState: { statusScope: "converted", reportTitle: "Proformas — Converted to Invoice" } }], isUp: true, icon: <DescriptionIcon />, color: "#0d9488", bg: "#f0fdfa", path: "/proforma-quote/list", filterState: { ...getCardFilterState("proforma"), statusScope: "not_cancelled", createdBy: summary.doc_created_by, reportTitle: "Proforma Report" }, paymentBreakdown: proformaPaymentBreakdown, paymentBreakdownAsCount: true },
    { title: "CDN Total", note: `Value of CDNs not yet invoiced · ${cdnDocCount} CDNs in total`, value: formatCurrency((summary.cdn_pending_total_value || 0) + (summary.cdn_pending_settled_value || 0)), change: `${summary.cdn_pending_doc_count ?? pendingGrnCount} Pending · ${summary.cdn_converted_count || 0} Converted to Invoice`, statusLinks: [{ label: `${summary.cdn_pending_doc_count ?? pendingGrnCount} Pending`, filterState: { statusScope: "open", reportTitle: "CDNs — Pending (not yet invoiced)" } }, { label: `${summary.cdn_converted_count || 0} Converted to Invoice`, filterState: { statusScope: "converted", reportTitle: "CDNs — Converted to Invoice" } }], isUp: true, icon: <ReceiptLongIcon />, color: "#d97706", bg: "#fffbeb", path: "/proforma-invoice/list", filterState: { ...getCardFilterState("cdn"), statusScope: "not_cancelled", createdBy: summary.doc_created_by, reportTitle: "Consignment Delivery Note (CDN) Report" }, paymentBreakdown: cdnPaymentBreakdown, paymentBreakdownAsCount: true },
    // Advance money already received on open CDNs (part-paid + fully paid in advance).
    { title: "CDN Received", value: formatCurrency((summary.cdn_pending_advance_held || 0) + (summary.cdn_pending_settled_value || 0)), change: "", isUp: true, icon: <PaymentsIcon />, color: "#16a34a", bg: "#f0fdf4", path: "/proforma-invoice/list", captionColor: "#16a34a",
      filterState: { statusScope: "open", advanceFilter: "any_advance", fromDate: getCardFilterState("cdn").fromDate, toDate: getCardFilterState("cdn").toDate, storeId: getCardFilterState("cdn").storeId, customerId: getCardFilterState("cdn").customerId, createdBy: summary.doc_created_by, reportTitle: "Open CDNs — Advance Received" },
      note: `${(summary.cdn_pending_partial_count || 0) + (summary.cdn_pending_settled_count || 0)} ${((summary.cdn_pending_partial_count || 0) + (summary.cdn_pending_settled_count || 0)) === 1 ? "CDN" : "CDNs"} with an advance · paid before invoicing`,
      valueBreakdown: [
        { label: "Fully paid in advance", count: summary.cdn_pending_settled_count || 0, value: summary.cdn_pending_settled_value || 0, color: "#16a34a", filterState: { advanceFilter: "settled", reportTitle: "Open CDNs — Fully Paid in Advance" } },
        { label: "Part-paid in advance", count: summary.cdn_pending_partial_count || 0, value: summary.cdn_pending_advance_held || 0, color: "#f59e0b", filterState: { advanceFilter: "with_advance", reportTitle: "Open CDNs — Part-Paid in Advance" } },
      ] },
    // Still to collect on open CDNs: Grand Total minus advances held, within
    // the selected period (choose "All Time" for every open CDN).
    { title: "CDN Pending", value: formatCurrency(summary.cdn_pending_amount || 0), change: "", isUp: false, icon: <PaymentsIcon />, color: "#b45309", bg: "#fffbeb", path: "/proforma-invoice/list", filterState: { statusScope: "open", advanceFilter: "pending", fromDate: getCardFilterState("cdn").fromDate, toDate: getCardFilterState("cdn").toDate, storeId: getCardFilterState("cdn").storeId, customerId: getCardFilterState("cdn").customerId, createdBy: summary.doc_created_by, reportTitle: "CDN Pending Amount" },
      // Open CDNs = to collect + advance already received + settled; the
      // legend's counts tally with the CDN card's "Pending" figure.
      captionColor: "#b45309",
      statusLinks: [
        { label: `${summary.cdn_pending_count || 0} Pending`, filterState: { advanceFilter: "pending", reportTitle: "Open CDNs — Pending" } },
      ],
      note: `${summary.cdn_pending_count || 0} ${(summary.cdn_pending_count || 0) === 1 ? "CDN" : "CDNs"} with a balance still to collect`,
      valueBreakdown: [
        { label: "To collect", count: summary.cdn_pending_count || 0, value: summary.cdn_pending_amount || 0, color: "#b45309", tooltip: `Pending ${formatCurrency(summary.cdn_pending_total_value || 0)} less advance`, filterState: { advanceFilter: "pending", reportTitle: "Open CDNs — To Be Collected" } },
      ] },
    // Advance money already received on open Proformas (part-paid + fully paid in advance).
    { title: "Proforma Received", value: formatCurrency((summary.proforma_pending_advance_held || 0) + (summary.proforma_pending_settled_value || 0)), change: "", isUp: true, icon: <PaymentsIcon />, color: "#16a34a", bg: "#f0fdf4", path: "/proforma-quote/list", captionColor: "#16a34a",
      filterState: { statusScope: "open", advanceFilter: "any_advance", fromDate: getCardFilterState("proforma").fromDate, toDate: getCardFilterState("proforma").toDate, storeId: getCardFilterState("proforma").storeId, customerId: getCardFilterState("proforma").customerId, createdBy: summary.doc_created_by, reportTitle: "Open Proformas — Advance Received" },
      note: `${(summary.proforma_pending_partial_count || 0) + (summary.proforma_pending_settled_count || 0)} ${((summary.proforma_pending_partial_count || 0) + (summary.proforma_pending_settled_count || 0)) === 1 ? "Proforma" : "Proformas"} with an advance · paid before invoicing`,
      valueBreakdown: [
        { label: "Fully paid in advance", count: summary.proforma_pending_settled_count || 0, value: summary.proforma_pending_settled_value || 0, color: "#16a34a", filterState: { advanceFilter: "settled", reportTitle: "Open Proformas — Fully Paid in Advance" } },
        { label: "Part-paid in advance", count: summary.proforma_pending_partial_count || 0, value: summary.proforma_pending_advance_held || 0, color: "#f59e0b", filterState: { advanceFilter: "with_advance", reportTitle: "Open Proformas — Part-Paid in Advance" } },
      ] },
    // Same card for Proformas: open Proformas' Grand Total minus advances held.
    { title: "Proforma Pending", value: formatCurrency(summary.proforma_pending_amount || 0), change: "", isUp: false, icon: <PaymentsIcon />, color: "#0f766e", bg: "#f0fdfa", path: "/proforma-quote/list", filterState: { statusScope: "open", advanceFilter: "pending", fromDate: getCardFilterState("proforma").fromDate, toDate: getCardFilterState("proforma").toDate, storeId: getCardFilterState("proforma").storeId, customerId: getCardFilterState("proforma").customerId, createdBy: summary.doc_created_by, reportTitle: "Proforma Pending Amount" },
      // Open Proformas = to collect + advance already received + settled; the
      // legend's counts tally with the Proforma card's "Pending" figure.
      captionColor: "#0f766e",
      statusLinks: [
        { label: `${summary.proforma_pending_count || 0} Pending`, filterState: { advanceFilter: "pending", reportTitle: "Open Proformas — Pending" } },
      ],
      note: `${summary.proforma_pending_count || 0} ${(summary.proforma_pending_count || 0) === 1 ? "Proforma" : "Proformas"} with a balance still to collect`,
      valueBreakdown: [
        { label: "To collect", count: summary.proforma_pending_count || 0, value: summary.proforma_pending_amount || 0, color: "#0f766e", tooltip: `Pending ${formatCurrency(summary.proforma_pending_total_value || 0)} less advance`, filterState: { advanceFilter: "pending", reportTitle: "Open Proformas — To Be Collected" } },
      ] },
    { title: "Active Customers", note: `${newCustomersGrowth.change} · placed an invoice in the period`, value: countCustomers, change: newCustomersGrowth.change, isUp: newCustomersGrowth.isUp, icon: <PeopleIcon />, color: "#0284c7", bg: "#f0f9ff", path: "/customer/list", captionColor: "#0284c7",
      filterState: { ...getCardFilterState("active_customers"), customerScope: "ordered", reportTitle: "Active Customers — placed an invoice in the period" },
      statusLinks: [
        { label: `${summary.active_customers ?? newCustomers} Active`, filterState: { customerScope: "ordered", reportTitle: "Active Customers — placed an invoice in the period" } },
        { label: `${summary.created_customers || 0} New (created)`, filterState: { customerScope: "created", reportTitle: "New Customers — created in the period" } },
      ] },
    { title: "Average Order Value", value: formatCurrency(aov), change: aovGrowth.change, isUp: aovGrowth.isUp, icon: <PointOfSaleIcon />, color: "#2563eb", bg: "#eff6ff", path: "/order/list", filterState: { ...getCardFilterState("aov"), reportTitle: "Average Order Value Report" } },
    {
      title: "FOC Requests",
      // Total FOC value; each line opens that tab of FOC Approvals, whose
      // "FOC value in this view" shows the same figure.
      value: formatCurrency(summary.foc_total_value || 0),
      change: `${focTotal} requests`,
      isUp: focPending === 0,
      icon: <CardGiftcardIcon />,
      color: "#7c3aed",
      bg: "#f5f3ff",
      path: "/foc/approvals",
      filterState: { ...getCardFilterState("foc"), statusTab: "all" },
      amountLines: [
        { label: `Pending (${focPending})`, value: summary.foc_pending_value || 0, color: "#d97706", filterState: { statusTab: "pending" } },
        { label: `Approved (${focConverted})`, value: summary.foc_approved_value || 0, color: "#16a34a", filterState: { statusTab: "approved" } },
        { label: `Rejected (${focRejected})`, value: summary.foc_rejected_value || 0, color: "#dc2626", filterState: { statusTab: "rejected" } },
        { label: `Total FOCs (${focTotal})`, value: summary.foc_total_value || 0, bold: true, color: "#7c3aed", filterState: { statusTab: "all" } },
      ],
    },
  ];

  const topProductsList = dashboardData.itemSales || [];
  const recentOrdersList = dashboardData.recentOrders || [];
  const salesTrendList = dashboardData.salesTrend || [];
  const topCustomersList = dashboardData.topCustomers || [];
  const inventoryAlerts = dashboardData.inventoryAlerts || {};
  const salesDistributionList = dashboardData.salesDistribution || [];
  const categorySalesList = dashboardData.categorySales || [];
  const channelSalesList = dashboardData.channelSales || [];
  const regionalSalesList = dashboardData.regionalSales || [];

  const findStoreId = (storeObj, storeName) => {
    if (storeObj?.store_id && parseInt(storeObj.store_id) > 0) return parseInt(storeObj.store_id);
    if (storeObj?.id && parseInt(storeObj.id) > 0) return parseInt(storeObj.id);
    if (storeName) {
      const match = stores.find(
        (s) => (s.store_name || s.name || "").toLowerCase().trim() === String(storeName).toLowerCase().trim()
      );
      if (match?.id) return parseInt(match.id);
      if (match?.store_id) return parseInt(match.store_id);
    }
    return storeId ? parseInt(storeId) : undefined;
  };

  // No fallback data — an empty salesDistributionList means there's no
  // real per-store breakdown for this account/period, and previously showed
  // a fabricated "Main Store / Branch Stores / Online Channels" 50/30/20
  // split instead of that (see the empty-state render below, matching the
  // Sales Trend chart's "No sales trend data..." pattern above).
  const distLabels = salesDistributionList.map((i) => i.store_name || "Store");
  const distValues = salesDistributionList.map((i) => parseFloat(i.total_sales || 0));

  return (
    <Layout>
      {/* ─── Global animation keyframes ─────────────────────────────────────── */}
      <GlobalStyles styles={{
        "@keyframes dashFadeSlideUp": {
          "0%": { opacity: 0, transform: "translateY(16px) scale(0.98)" },
          "100%": { opacity: 1, transform: "translateY(0)   scale(1)" },
        },
        "@keyframes dashFadeIn": {
          "0%": { opacity: 0 },
          "100%": { opacity: 1 },
        },
        "@keyframes dashSpin": {
          "0%": { transform: "rotate(0deg)" },
          "100%": { transform: "rotate(360deg)" },
        },
        "@keyframes dashNumberPop": {
          "0%": { opacity: 0.15, transform: "translateY(4px) scale(0.96)" },
          "100%": { opacity: 1, transform: "translateY(0)   scale(1)" },
        },
        /* ── Respect prefers-reduced-motion ────────────────────────────────── */
        "@media (prefers-reduced-motion: reduce)": {
          "*, *::before, *::after": {
            animationDuration: "0.01ms !important",
            animationIterationCount: "1 !important",
            transitionDuration: "0.01ms !important",
          },
        },
      }} />
      {/* ─────────────────────────────────────────────────────────────────────── */}

      <Box sx={{
        background: "#f8fafc",
        minHeight: "100vh",
        pt: "85px",
        pb: 6,
        px: { xs: 2, md: 3.5 },
        opacity: mounted ? 1 : 0,
        transition: "opacity 0.35s ease-out",
      }}>

        {loading && (
          <LinearProgress
            sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 2000, height: 3 }}
          />
        )}

        {/* HEADER BAR & FILTERS */}
        <Box
          display="flex"
          flexDirection="column"
          gap={1.75}
          mb={3}
          sx={{ animation: mounted ? "dashFadeSlideUp 0.45s ease-out both" : "none" }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box sx={{ width: 44, height: 44, borderRadius: 2.5, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", background: "linear-gradient(135deg, #1d4ed8, #3b82f6)", boxShadow: "0 6px 14px -6px rgba(37,99,235,0.6)" }}>
                <PointOfSaleIcon />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight="800" color="#0f172a" sx={{ fontSize: "1.4rem", letterSpacing: "-0.01em", lineHeight: 1.2 }}>
                  Sales Overview Dashboard
                </Typography>
                <Typography variant="body2" color="#64748b">
                  Welcome back, {userData?.username || "there"} 👋
                </Typography>
              </Box>
            </Box>
            <Box sx={{ px: 1.5, py: 0.6, borderRadius: 5, backgroundColor: "#eff6ff", color: "#1d4ed8", fontSize: "0.72rem", fontWeight: 700, border: "1px solid #dbeafe" }}>
              {moment().format("dddd, DD-MM-YYYY")}
            </Box>
          </Box>

          {/* Filter toolbar — one rounded bar instead of loose controls */}
          <Box
            display="flex"
            flexDirection="column"
            gap={1.5}
            sx={{ p: 2, borderRadius: 3, backgroundColor: "#fff", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(15,23,42,0.04)" }}
          >
            <Typography sx={{ fontSize: "0.66rem", fontWeight: 800, letterSpacing: 0.8, color: "#94a3b8", textTransform: "uppercase", pr: 0.5 }}>
              Filters
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(3, minmax(0, 1fr))",
                  xl: "repeat(6, minmax(0, 1fr))",
                },
                gap: 1.5,
                alignItems: "center",
                // Every control fills its grid cell (they used to carry their own fixed widths).
                "& .MuiAutocomplete-root, & .MuiTextField-root": { width: "100% !important" },
                '& input[type="date"]': { flex: 1, minWidth: 0 },
              }}
            >
            <Autocomplete
              size="small"
              options={[
                { value: "all", label: "All Time" },
                { value: "daily", label: "Today" },
                { value: "weekly", label: "Weekly" },
                { value: "monthly", label: "This Month" },
                { value: "yearly", label: "Yearly" },
              ]}
              getOptionLabel={(option) => option.label || ""}
              value={
                [
                  { value: "all", label: "All Time" },
                  { value: "daily", label: "Today" },
                  { value: "weekly", label: "Weekly" },
                  { value: "monthly", label: "This Month" },
                  { value: "yearly", label: "Yearly" },
                ].find((p) => p.value === period) || { value: "monthly", label: "This Month" }
              }
              onChange={(event, newValue) => {
                if (newValue) {
                  handlePeriodChange(newValue.value);
                }
              }}
              isOptionEqualToValue={(option, val) => option.value === val.value}
              disableClearable
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Period"
                  sx={{
                    width: 140,
                    backgroundColor: "#fff",
                    borderRadius: 1.5,
                    "& .MuiOutlinedInput-root": {
                      height: 38,
                      borderRadius: 1.5,
                      border: "1px solid #cbd5e1",
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      "& fieldset": { border: "none" },
                    },
                  }}
                />
              )}
            />

            <Autocomplete
              size="small"
              options={[{ id: 0, store_name: "All Stores" }, ...stores]}
              getOptionLabel={(option) => option.store_name || ""}
              value={
                [{ id: 0, store_name: "All Stores" }, ...stores].find(
                  (s) => Number(s.id) === Number(storeId)
                ) || { id: 0, store_name: "All Stores" }
              }
              onChange={(event, newValue) => {
                setStoreId(newValue ? newValue.id : 0);
              }}
              isOptionEqualToValue={(option, val) => Number(option.id) === Number(val.id)}
              disableClearable
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Store"
                  sx={{
                    width: 170,
                    backgroundColor: "#fff",
                    borderRadius: 1.5,
                    "& .MuiOutlinedInput-root": {
                      height: 38,
                      borderRadius: 1.5,
                      border: "1px solid #cbd5e1",
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      "& fieldset": { border: "none" },
                    },
                  }}
                />
              )}
            />

            <Autocomplete
              size="small"
              options={[{ customer_id: 0, name: "All Customers / Companies" }, ...customersList]}
              getOptionLabel={(option) => option.name || ""}
              value={
                [{ customer_id: 0, name: "All Customers / Companies" }, ...customersList].find(
                  (c) => Number(c.customer_id) === Number(customerId)
                ) || { customer_id: 0, name: "All Customers / Companies" }
              }
              onChange={(event, newValue) => {
                setCustomerId(newValue ? newValue.customer_id : 0);
              }}
              isOptionEqualToValue={(option, val) => Number(option.customer_id) === Number(val.customer_id)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Customer / Company"
                  sx={{
                    width: 210,
                    backgroundColor: "#fff",
                    borderRadius: 1.5,
                    "& .MuiOutlinedInput-root": {
                      height: 38,
                      borderRadius: 1.5,
                      border: "1px solid #cbd5e1",
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      "& fieldset": { border: "none" },
                    },
                  }}
                />
              )}
            />

            <Autocomplete
              size="small"
              options={[{ id: 0, name: "All Salespersons" }, ...salesPersonsList]}
              getOptionLabel={(option) => option.name || ""}
              value={
                [{ id: 0, name: "All Salespersons" }, ...salesPersonsList].find(
                  (sp) => Number(sp.id) === Number(salesPersonId)
                ) || { id: 0, name: "All Salespersons" }
              }
              onChange={(event, newValue) => {
                setSalesPersonId(newValue ? newValue.id : 0);
              }}
              isOptionEqualToValue={(option, val) => Number(option.id) === Number(val.id)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Salesperson"
                  sx={{
                    width: 190,
                    backgroundColor: "#fff",
                    borderRadius: 1.5,
                    "& .MuiOutlinedInput-root": {
                      height: 38,
                      borderRadius: 1.5,
                      border: "1px solid #cbd5e1",
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      "& fieldset": { border: "none" },
                    },
                  }}
                />
              )}
            />

            <Box
              display="flex"
              alignItems="center"
              sx={{
                height: 38,
                backgroundColor: "#fff",
                borderRadius: 1.5,
                border: "1px solid #cbd5e1",
                px: 1.2,
              }}
            >
              <Typography variant="caption" fontWeight="700" color="#64748b" sx={{ fontSize: "0.75rem", mr: 0.8, flexShrink: 0 }}>
                From:
              </Typography>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                style={{
                  border: "none",
                  outline: "none",
                  backgroundColor: "transparent",
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  color: "#1e293b",
                  fontFamily: "inherit",
                  cursor: "pointer",
                }}
              />
            </Box>

            <Box
              display="flex"
              alignItems="center"
              sx={{
                height: 38,
                backgroundColor: "#fff",
                borderRadius: 1.5,
                border: "1px solid #cbd5e1",
                px: 1.2,
              }}
            >
              <Typography variant="caption" fontWeight="700" color="#64748b" sx={{ fontSize: "0.75rem", mr: 0.8, flexShrink: 0 }}>
                To:
              </Typography>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                style={{
                  border: "none",
                  outline: "none",
                  backgroundColor: "transparent",
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  color: "#1e293b",
                  fontFamily: "inherit",
                  cursor: "pointer",
                }}
              />
            </Box>

            </Box>

            {/* Search takes the free width; Clear and Refresh sit at the end of the row. */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
                "& .MuiTextField-root": { width: "auto !important", flex: 1, minWidth: 220 },
              }}
            >
            {/* Clear resets EVERY filter to "all": All Time, All Stores, All Customers,
                All Salespersons, no dates, empty search. Shown whenever any filter is narrowing the view. */}
            <Tooltip title="Search by Invoice No., Product Name/SKU, Store, Customer / Company, Salesperson, or Status" arrow placement="top">
              <TextField
                placeholder="Search invoice, customer/company, store..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchSubmit}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "#94a3b8" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchQuery("")}
                        sx={{ p: 0.2, color: "#94a3b8", "&:hover": { color: "#ef4444" } }}
                      >
                        <ClearIcon fontSize="small" sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : (
                    <InputAdornment position="end">
                      <InfoOutlinedIcon fontSize="small" sx={{ color: "#94a3b8" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: 230,
                  backgroundColor: "#fff",
                  borderRadius: 1.5,
                  "& .MuiOutlinedInput-root": {
                    height: 38,
                    borderRadius: 1.5,
                    border: "1px solid #cbd5e1",
                    fontSize: "0.78rem",
                    fontWeight: 500,
                    "& fieldset": { border: "none" },
                  },
                }}
              />
            </Tooltip>

            {(period !== "all" ||
              Number(storeId) > 0 ||
              Number(customerId) > 0 ||
              Number(salesPersonId) > 0 ||
              fromDate ||
              toDate ||
              searchQuery) && (
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={() => {
                  handlePeriodChange("all"); // period + the date range + every card's own period
                  setStoreId(0);
                  setCustomerId(0);
                  setSalesPersonId(0);
                  setSearchQuery("");
                }}
                sx={{
                  height: 38,
                  borderRadius: 1.5,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.78rem",
                  px: 2,
                  backgroundColor: "#fff",
                  border: "1px solid #c084fc",
                }}
              >
                Clear
              </Button>
            )}

            <IconButton
              color="primary"
              onClick={async () => {
                setRefreshing(true);
                await fetchDashboardData();
                setRefreshing(false);
              }}
              sx={{
                height: 38,
                width: 38,
                backgroundColor: "#fff",
                border: "1px solid #cbd5e1",
                borderRadius: 1.5,
                p: 0,
                transition: "background-color 0.2s ease, box-shadow 0.2s ease",
                "&:hover": { backgroundColor: "#f1f5f9", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
              }}
            >
              <RefreshIcon
                fontSize="small"
                sx={{
                  animation: refreshing ? "dashSpin 0.7s linear infinite" : "none",
                  transition: "color 0.2s ease",
                }}
              />
            </IconButton>
            </Box>

            {/* Shown while a search is active and has results (the "no results" panel below replaces it otherwise). */}
            {searching && !searchNoMatch && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.9, borderRadius: 2, backgroundColor: "#EFF6FF", border: "1px solid #DBEAFE" }}>
                <InfoOutlinedIcon sx={{ fontSize: 17, color: "#2563EB", flexShrink: 0 }} />
                <Typography sx={{ fontSize: "0.76rem", color: "#1E3A8A", fontWeight: 600 }}>
                  Showing invoice results for “{String(searchQuery).trim()}”. Other cards are hidden while you search — clear the search to see everything.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* ALL DOCUMENTS COMBINED — Invoices + CDN + Proforma. Total = Received + Pending. Hidden while searching
            (a text search only narrows invoices, so the combined figures would not add up). */}
        {!searching && (() => {
          const n = (v) => parseFloat(v || 0);
          const parts = [
            {
              key: "invoice",
              label: "Invoices",
              total: n(summary.period_sales ?? summary.monthly_sales),
              received: n(summary.collections_received),
              pending: n(summary.pending_collections),
            },
            {
              key: "cdn",
              label: "CDN",
              total: n(summary.cdn_pending_total_value) + n(summary.cdn_pending_settled_value),
              received: n(summary.cdn_pending_advance_held) + n(summary.cdn_pending_settled_value),
              pending: n(summary.cdn_pending_amount),
            },
            {
              key: "proforma",
              label: "Proforma",
              total: n(summary.proforma_pending_total_value) + n(summary.proforma_pending_settled_value),
              received: n(summary.proforma_pending_advance_held) + n(summary.proforma_pending_settled_value),
              pending: n(summary.proforma_pending_amount),
            },
          ];
          const sum = (k) => parts.reduce((a, x) => a + x[k], 0);
          const tiles = [
            { key: "total", title: "Total", sub: "Billed + not yet invoiced", color: "#2563eb", bg: "#eff6ff", icon: <ReceiptLongIcon sx={{ fontSize: 18 }} /> },
            { key: "received", title: "Received", sub: "Collected + paid in advance", color: "#16a34a", bg: "#f0fdf4", icon: <PaymentsIcon sx={{ fontSize: 18 }} /> },
            { key: "pending", title: "Pending", sub: "Still to collect", color: "#ea580c", bg: "#fff7ed", icon: <AccessTimeIcon sx={{ fontSize: 18 }} /> },
          ];
          return (
            <Box
              sx={{
                mt: 2,
                mb: 2.5,
                display: "grid",
                gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(3, minmax(0, 1fr))" },
                borderRadius: 3,
                backgroundColor: "#fff",
                border: "1px solid #e2e8f0",
                boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
                overflow: "hidden",
              }}
            >
              {tiles.map((t, idx) => (
                <Box
                  key={t.key}
                  sx={{
                    px: 2.5,
                    py: 1.8,
                    borderLeft: { md: idx > 0 ? "1px solid #eef2f7" : "none" },
                    borderTop: { xs: idx > 0 ? "1px solid #eef2f7" : "none", md: "none" },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={0.6}>
                    <Box sx={{ width: 28, height: 28, borderRadius: 1.5, backgroundColor: t.bg, color: t.color, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {t.icon}
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: "0.78rem", fontWeight: 800, color: "#0f172a", lineHeight: 1.1 }}>
                        {t.title}
                      </Typography>
                      <Typography sx={{ fontSize: "0.62rem", color: "#94a3b8", lineHeight: 1.2 }}>{t.sub}</Typography>
                    </Box>
                  </Box>
                  {loading ? (
                    <ShinySkeleton width={150} height={30} borderRadius={6} />
                  ) : (
                    <Typography sx={{ fontSize: "1.55rem", fontWeight: 800, color: t.color, lineHeight: 1.15 }}>
                      {formatCurrency(sum(t.key))}
                    </Typography>
                  )}
                  <Box display="flex" flexWrap="wrap" columnGap={1.6} rowGap={0.2} mt={0.6}>
                    {parts.map((x) => (
                      <Typography key={x.key} sx={{ fontSize: "0.68rem", color: "#64748b" }}>
                        {x.label} <b style={{ color: "#334155" }}>{loading ? "…" : formatCurrency(x[t.key])}</b>
                      </Typography>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          );
        })()}

        {searchNoMatch && (
          <Box
            sx={{
              mt: 1,
              mb: 3,
              py: 7,
              px: 3,
              textAlign: "center",
              borderRadius: 3,
              backgroundColor: "#fff",
              border: "1px dashed #CBD5E1",
            }}
          >
            <SearchIcon sx={{ fontSize: 44, color: "#94A3B8", mb: 1 }} />
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 800, color: "#0F172A" }}>
              No results found for “{String(searchQuery).trim()}”
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: "#64748B", mt: 0.75, maxWidth: 520, mx: "auto", lineHeight: 1.55 }}>
              No invoice, customer, company, store, salesperson or product matched. Check the spelling, or widen the
              period and filters above and try again.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setSearchQuery("")}
              sx={{ mt: 2, textTransform: "none", fontWeight: 700, borderRadius: "10px" }}
            >
              Clear search
            </Button>
          </Box>
        )}

        {!searchNoMatch && (
        <>
        {/* 1. BALANCED 8 KPI STAT CARDS GRID — 4 per row so each card has
            enough width for its full label and value; wraps to 2 rows on
            desktop instead of cramming 8 across into an unreadable strip. */}
        {/* KPI cards grouped into Invoice / CDN / Proforma sections, then the
            independent cards (New Customers, Average Order Value, FOC). */}
        {(() => {
          const renderStatCard = (card, idx) => (
            <Card
              key={idx}
              elevation={0}
              sx={{
                p: 1.75,
                borderRadius: 3,
                border: "1px solid #e2e8f0",
                backgroundColor: "#ffffff",
                minHeight: 108,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                transition: "all 0.22s cubic-bezier(0.4, 0, 0.2, 1)",
                position: "relative",
                animation: mounted ? `dashFadeSlideUp 0.4s ease-out ${idx * 55}ms both` : "none",
                "&:hover": {
                  transform: "translateY(-4px) scale(1.015)",
                  boxShadow: "0 14px 30px -6px rgba(15, 23, 42, 0.10)",
                  borderColor: "#cbd5e1",
                },
              }}
            >
              <Box
                onClick={() => card.path && navigate(card.path, { state: card.filterState })}
                sx={{ cursor: card.path ? "pointer" : "default" }}
              >
                <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      backgroundColor: card.color,
                      color: "#ffffff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {React.cloneElement(card.icon, { sx: { fontSize: 19 } })}
                  </Box>
                  <Box display="flex" flexDirection="column" minWidth={0} flex={1}>
                    <Typography
                      variant="caption"
                      fontWeight="600"
                      color="#64748b"
                      sx={{
                        fontSize: "0.7rem",
                        lineHeight: 1.25,
                      }}
                    >
                      {card.title}
                    </Typography>
                    {card.subtitle && (
                      <Typography sx={{ fontSize: "0.62rem", color: "#94a3b8", lineHeight: 1.2 }}>
                        {card.subtitle}
                      </Typography>
                    )}
                    <Typography
                      key={`val-${idx}-${card.value}`}
                      variant="h6"
                      fontWeight="800"
                      color="#0f172a"
                      sx={{
                        fontSize: { xs: "0.95rem", xl: "1.05rem" },
                        mt: 0.2,
                        letterSpacing: "-0.3px",
                        animation: !loading ? "dashNumberPop 0.35s ease-out both" : "none",
                      }}
                    >
                      {loading ? <ShinySkeleton width={65} height={16} borderRadius={4} /> : typeof card.value === "number" ? formatNumber(card.value) : card.value}
                    </Typography>
                  </Box>
                </Box>

                <Typography
                  variant="caption"
                  fontWeight="600"
                  color={card.captionColor || (card.isUp ? "#16a34a" : "#dc2626")}
                  sx={{
                    fontSize: "0.66rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.3,
                    mt: 0.75,
                  }}
                >
                  {loading ? (
                    <ShinySkeleton width={80} height={12} borderRadius={4} />
                  ) : Array.isArray(card.statusLinks) && card.path ? (
                    // Pending / Converted shown as separate links, each opening
                    // the list on exactly those documents.
                    <Box component="span" sx={{ display: "inline-flex", flexWrap: "wrap", alignItems: "center", gap: 0.5 }}>
                      {card.statusLinks.map((link, li) => (
                        <React.Fragment key={link.label}>
                          {li > 0 && <span>·</span>}
                          <Box
                            component="span"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(card.path, { state: { ...card.filterState, ...link.filterState } });
                            }}
                            sx={{ cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2, "&:hover": { opacity: 0.75 } }}
                          >
                            {link.label}
                          </Box>
                        </React.Fragment>
                      ))}
                    </Box>
                  ) : card.change}
                </Typography>
                {!loading && card.note && (
                  <Typography sx={{ fontSize: "0.62rem", color: "#94a3b8", fontWeight: 600, mt: 0.25 }}>
                    {card.note}
                  </Typography>
                )}
              </Box>

              {/* Payment-method split, shown only on the Today's Sales / Total
                  Sales cards — reflects the same period/store filters as the
                  card's own total. Rendered as a segmented bar + legend
                  rather than loose chips. */}
              {!loading &&
                card.paymentBreakdown &&
                card.paymentBreakdown.length > 0 &&
                renderPaymentSplitBar(card.paymentBreakdown, card.path, card.filterState, card.paymentBreakdownAsCount)}

              {!loading && card.valueBreakdown && renderValueSplitBar(card.valueBreakdown, card.path, card.filterState, card.emptyText)}

              {/* Status split (e.g. FOC Requests' Pending / Converted /
                  Rejected) — same segmented-bar widget, driven by counts. */}
              {!loading &&
                card.statusBreakdown &&
                card.statusBreakdown.length > 0 &&
                renderStatusSplitBar(card.statusBreakdown, card.path, card.filterState)}

              {/* Labelled amount lines (e.g. CDN Pending Amount's breakdown of
                  what's still to be collected). */}
              {!loading && Array.isArray(card.amountLines) && card.amountLines.length > 0 && (
                <Box sx={{ mt: 1, pt: 1, borderTop: "1px dashed #e2e8f0", display: "grid", gap: 0.4 }}>
                  {card.amountLines.map((line) => line.header ? (
                    <Typography key={line.label} sx={{ fontSize: "0.62rem", color: "#94a3b8", fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase", px: 0.3 }}>
                      {line.label}
                    </Typography>
                  ) : (
                    <Box
                      key={line.label}
                      // A line with its own filterState drills into the card's
                      // list with that filter; stopPropagation keeps the card's
                      // own click from firing too.
                      onClick={
                        line.filterState && card.path
                          ? (e) => {
                              e.stopPropagation();
                              navigate(card.path, { state: { ...card.filterState, ...line.filterState } });
                            }
                          : undefined
                      }
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 1,
                        px: 0.3,
                        pl: 0.3 + (line.indent || 0) * 1.3,
                        borderRadius: 1,
                        cursor: line.filterState ? "pointer" : "default",
                        "&:hover": line.filterState ? { backgroundColor: "#f1f5f9" } : undefined,
                      }}
                    >
                      <Typography sx={{ fontSize: "0.7rem", color: "#64748b", fontWeight: line.bold ? 700 : 500 }}>
                        {line.label}
                      </Typography>
                      <Typography sx={{ fontSize: "0.7rem", color: line.color || "#334155", fontWeight: line.bold ? 700 : 600, whiteSpace: "nowrap" }}>
                        {formatCurrency(line.value)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Card>

          );

          const byTitle = (t) => statCards.find((c) => c.title === t);
          const sections = [
            { key: "invoice", label: "Invoices", sub: "Sales, collections and outstanding balances", icon: <ReceiptLongIcon sx={{ fontSize: 18 }} />, titles: [salesCardTitle, "Collections Received", "Pending Collections"] },
            { key: "cdn", label: "CDN", sub: "Consignment delivery notes and amounts still to collect", icon: <AssignmentTurnedInIcon sx={{ fontSize: 18 }} />, titles: ["CDN Total", "CDN Received", "CDN Pending"] },
            { key: "proforma", label: "Proforma", sub: "Proforma invoices and amounts still to collect", icon: <DescriptionIcon sx={{ fontSize: 18 }} />, titles: ["Proforma Total", "Proforma Received", "Proforma Pending"] },
          ];
          const independent = ["Active Customers", "Average Order Value", "FOC Requests"];
          // Columns follow the available width (not the screen), so cards never
          // get squeezed when the sidebar is open or the window is narrow.
          const gridSx = (cols) => ({
            display: "grid",
            gridTemplateColumns: `repeat(auto-fit, minmax(${cols >= 3 ? 230 : 260}px, 1fr))`,
            gap: 1.5,
          });
          let n = 0;
          return (
            <>
              {!searching && cancelledRecv.total > 0 && (
                <Box
                  mb={2.5}
                  sx={{ p: 2, borderRadius: 3, backgroundColor: "#fef2f2", border: "1px solid #fecaca" }}
                >
                  <Box display="flex" alignItems="center" gap={1.25} mb={1.5}>
                    <Box sx={{ width: 32, height: 32, borderRadius: 2, backgroundColor: "#fff", border: "1px solid #fecaca", color: "#dc2626", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <CancelIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>
                        Cancelled — payment received
                      </Typography>
                      <Typography sx={{ fontSize: "0.7rem", color: "#64748b" }}>
                        Cancelled documents are kept on record; this is money already received against them
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 1.5 }}>
                    {[
                      { key: "all", label: "Total", count: cancelledRecv.count, value: cancelledRecv.total, strong: true },
                      { key: "invoice", label: "Invoices", ...(cancelledRecv.summary?.invoice || {}) },
                      { key: "cdn", label: "CDNs", ...(cancelledRecv.summary?.cdn || {}) },
                      { key: "proforma", label: "Proformas", ...(cancelledRecv.summary?.proforma || {}) },
                    ]
                      .filter((c) => c.strong || (c.total ?? c.value) > 0)
                      .map((c) => (
                        <Box
                          key={c.key}
                          onClick={() => openCancelledCard(c.key)}
                          sx={{ cursor: "pointer", p: 1.5, borderRadius: 2, backgroundColor: "#fff", border: "1px solid #fecaca", "&:hover": { borderColor: "#f87171", boxShadow: "0 6px 16px -6px rgba(220,38,38,0.25)" } }}
                        >
                          <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            {c.label} ({c.count})
                          </Typography>
                          <Typography sx={{ fontSize: c.strong ? "1.35rem" : "1.1rem", fontWeight: 800, color: "#dc2626" }}>
                            {formatCurrency(c.total ?? c.value)}
                          </Typography>
                        </Box>
                      ))}
                  </Box>
                </Box>
              )}
              <CancelledReceivedDialog
                open={Boolean(cancelledTypeOpen)}
                type={cancelledTypeOpen && cancelledTypeOpen !== "all" ? cancelledTypeOpen : undefined}
                filters={cancelledFilters}
                onClose={() => setCancelledTypeOpen(null)}
              />
              {sections.filter((sec) => !searching || sec.key === "invoice").map((sec) => (
                <Box
                  key={sec.key}
                  mb={2.5}
                  sx={{ p: 2, borderRadius: 3, backgroundColor: "#f1f5f9", border: "1px solid #e2e8f0" }}
                >
                  <Box display="flex" alignItems="center" gap={1.25} mb={1.5}>
                    <Box sx={{ width: 32, height: 32, borderRadius: 2, backgroundColor: "#fff", border: "1px solid #e2e8f0", color: "#334155", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {sec.icon}
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>
                        {sec.label}
                      </Typography>
                      <Typography sx={{ fontSize: "0.7rem", color: "#64748b" }}>{sec.sub}</Typography>
                    </Box>
                  </Box>
                  <Box sx={gridSx(sec.titles.length === 3 ? 3 : 2)}>
                    {sec.titles.map((t) => byTitle(t)).filter(Boolean).map((card) => renderStatCard(card, n++))}
                  </Box>
                </Box>
              ))}
              <Box sx={{ ...gridSx(3), mb: 3 }}>
                {independent.filter((t) => !searching || t !== "FOC Requests").map((t) => byTitle(t)).filter(Boolean).map((card) => renderStatCard(card, n++))}
              </Box>
            </>
          );
        })()}

        {/* 2. MIDDLE ROW 1: SALES TREND + TOP SELLING PRODUCTS + SALES DISTRIBUTION */}
        <Grid container spacing={2.5} mb={3}>
          {/* Sales Trend Chart */}
          <Grid item xs={12} lg={5}>
            <Card elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #e2e8f0", backgroundColor: "#fff", height: 360, display: "flex", flexDirection: "column", justifyContent: "space-between", boxShadow: "0 1px 3px rgba(0,0,0,0.02)", transition: "box-shadow 0.2s ease, border-color 0.2s ease", "&:hover": { boxShadow: "0 6px 24px -4px rgba(15,23,42,0.08)", borderColor: "#cbd5e1" }, animation: mounted ? "dashFadeSlideUp 0.45s ease-out 100ms both" : "none" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle1" fontWeight="700" color="#0f172a">
                  Sales Trend
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Select
                    size="small"
                    value={trendPeriod}
                    onChange={(e) => handleTrendPeriodChange(e.target.value)}
                    sx={{
                      fontSize: "0.72rem",
                      height: 28,
                      backgroundColor: "#fff",
                      borderRadius: 1.5,
                      border: "1px solid #e2e8f0",
                      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    }}
                  >
                    <MenuItem value="all">All Time</MenuItem>
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">This Month</MenuItem>
                    <MenuItem value="yearly">Yearly</MenuItem>
                  </Select>
                </Box>
              </Box>

              {trendLoading ? (
                <Box px={1} py={2}>
                  <ShinySkeleton height={220} borderRadius={8} />
                </Box>
              ) : salesTrendList.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={260}>
                  <Typography variant="body2" color="text.secondary">
                    No sales trend data recorded for selected filters.
                  </Typography>
                </Box>
              ) : (
                <Chart
                  options={{
                    chart: {
                      toolbar: { show: false },
                      animations: {
                        enabled: true,
                        easing: "easeinout",
                        speed: 600,
                        animateGradually: { enabled: true, delay: 80 },
                        dynamicAnimation: { enabled: true, speed: 400 },
                      },
                      events: {
                        dataPointSelection: (event, chartContext, config) => {
                          const idx = config?.dataPointIndex;
                          if (idx !== undefined && idx >= 0 && salesTrendList[idx]) {
                            const trendItem = salesTrendList[idx];
                            // Real first/last invoice date of the clicked bucket (the label,
                            // e.g. "30 Oct" or "13-19 Oct", is not a date the list can use).
                            const itemFrom = trendItem.date_from || trendItem.date || trendItem.order_date;
                            const itemTo = trendItem.date_to || trendItem.date || trendItem.order_date;
                            const itemDate = itemFrom || trendItem.day_label;
                            setTimeout(() => {
                              navigate("/order/list", {
                                state: {
                                  fromDate: itemFrom || fromDate,
                                  toDate: itemTo || toDate,
                                  storeId,
                                  customerId,
                                  salesPersonId,
                                  reportTitle: `Sales Trend: ${trendItem.day_label || itemDate}`,
                                },
                              });
                            }, 60);
                          }
                        },
                      },
                    },
                    stroke: { width: [0, 3], curve: "smooth" },
                    plotOptions: { bar: { columnWidth: "50%", borderRadius: 4 } },
                    colors: ["#6366f1", "#06b6d4"],
                    xaxis: {
                      categories: salesTrendList.map((i) => i.day_label),
                      labels: { rotate: -30, style: { fontSize: "10px", fontWeight: 600 } },
                    },
                    yaxis: { labels: { formatter: (val) => formatCurrency(val) } },
                    legend: { position: "top", horizontalAlign: "left" },
                    grid: { borderColor: "#f1f5f9" },
                    tooltip: { y: { formatter: (val) => formatCurrency(val) } },
                  }}
                  series={[
                    { name: "Sales (AED)", type: "column", data: salesTrendList.map((i) => parseFloat(i.revenue || 0)) },
                  ]}
                  type="line"
                  height={260}
                />
              )}
            </Card>
          </Grid>

          {/* Top Selling Products */}
          <Grid item xs={12} lg={4}>
            <Card elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: "1px solid #e2e8f0", backgroundColor: "#fff", height: 360, display: "flex", flexDirection: "column", transition: "box-shadow 0.2s ease, border-color 0.2s ease", "&:hover": { boxShadow: "0 6px 24px -4px rgba(15,23,42,0.08)", borderColor: "#cbd5e1" }, animation: mounted ? "dashFadeSlideUp 0.45s ease-out 160ms both" : "none" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="subtitle1" fontWeight="700" color="#1e293b" sx={{ fontSize: "0.95rem" }}>
                    Top Selling Products
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => navigate("/product/list", { state: getCardFilterState("top_products") })}
                    sx={{
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      textTransform: "none",
                      color: "#2563eb",
                      p: 0,
                      minWidth: "auto",
                      "&:hover": { textDecoration: "underline", backgroundColor: "transparent" },
                    }}
                  >
                    View All
                  </Button>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Select
                    size="small"
                    value={productSort}
                    onChange={(e) => handleProductSortChange(e.target.value)}
                    sx={{
                      fontSize: "0.72rem",
                      height: 28,
                      backgroundColor: "#fff",
                      borderRadius: 1.5,
                      border: "1px solid #e2e8f0",
                      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    }}
                  >
                    <MenuItem value="top">High</MenuItem>
                    <MenuItem value="bottom">Low</MenuItem>
                  </Select>
                  <Select
                    size="small"
                    value={productsPeriod}
                    onChange={(e) => handleProductsPeriodChange(e.target.value)}
                    sx={{
                      fontSize: "0.72rem",
                      height: 28,
                      backgroundColor: "#fff",
                      borderRadius: 1.5,
                      border: "1px solid #e2e8f0",
                      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    }}
                  >
                    <MenuItem value="all">All Time</MenuItem>
                    <MenuItem value="daily">Today</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">This Month</MenuItem>
                    <MenuItem value="yearly">Yearly</MenuItem>
                  </Select>
                </Box>
              </Box>

              <Box display="flex" alignItems="center" pb={1} mb={1} sx={{ borderBottom: "1px solid #f1f5f9" }}>
                <Box width="52%" display="flex" alignItems="center">
                  <Typography variant="caption" fontWeight="600" color="#64748b" sx={{ fontSize: "0.68rem", whiteSpace: "nowrap" }}>
                    Product
                  </Typography>
                </Box>
                <Box width="18%" textAlign="center">
                  <Typography variant="caption" fontWeight="600" color="#64748b" sx={{ fontSize: "0.68rem", whiteSpace: "nowrap" }}>
                    Qty Sold
                  </Typography>
                </Box>
                <Box width="30%" textAlign="right">
                  <Typography variant="caption" fontWeight="600" color="#64748b" sx={{ fontSize: "0.68rem", whiteSpace: "nowrap" }}>
                    Revenue (AED)
                  </Typography>
                </Box>
              </Box>

              {productsLoading ? (
                <Box display="flex" flexDirection="column" gap={1.5} py={1}>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <ShinySkeleton key={i} height={32} borderRadius={6} />
                  ))}
                </Box>
              ) : topProductsList.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1}>
                  <Typography variant="body2" color="text.secondary">
                    No product sales recorded.
                  </Typography>
                </Box>
              ) : (
                <Box display="flex" flexDirection="column" gap={1.2} sx={{ overflowY: "auto", pr: 0.5, flexGrow: 1 }}>
                  {topProductsList.map((row, idx) => (
                    <Box
                      key={row.product_id || idx}
                      display="flex"
                      alignItems="center"
                      py={0.4}
                      onClick={() =>
                        hasPermission(3) &&
                        navigate("/product/list", {
                          state: {
                            search: row.product_name,
                            productName: row.product_name,
                            reportTitle: `Product: ${row.product_name}`,
                          },
                        })
                      }
                      sx={{
                        cursor: hasPermission(3) ? "pointer" : "default",
                        "&:hover": { backgroundColor: "#f8fafc" },
                        borderRadius: 1.5,
                        px: 0.5,
                        transition: "background-color 0.18s ease",
                        animation: mounted ? `dashFadeSlideUp 0.28s ease-out ${Math.min(idx, 7) * 38}ms both` : "none",
                      }}
                    >
                      <Box width="52%" display="flex" alignItems="center" gap={0.8} overflow="hidden">
                        <Typography variant="caption" fontWeight="700" color="#1e293b" sx={{ fontSize: "0.72rem", width: 12, flexShrink: 0 }}>
                          {idx + 1}
                        </Typography>
                        <Avatar
                          src={getProductImageUrl(row.product_image || row.image)}
                          alt={row.product_name}
                          variant="rounded"
                          sx={{
                            width: 26,
                            height: 26,
                            borderRadius: 1.5,
                            backgroundColor: "#f1f5f9",
                            flexShrink: 0,
                            fontSize: "0.75rem",
                            border: "1px solid #e2e8f0",
                            "& img": { objectFit: "cover" },
                          }}
                        >
                          📦
                        </Avatar>
                        <Tooltip title={row.product_name || ""} arrow placement="top">
                          <Typography
                            variant="body2"
                            fontWeight="600"
                            color="#1e293b"
                            sx={{
                              fontSize: "0.72rem",
                              lineHeight: 1.15,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              wordBreak: "break-word",
                              cursor: "pointer",
                            }}
                          >
                            {row.product_name}
                          </Typography>
                        </Tooltip>
                      </Box>

                      <Box width="18%" textAlign="center">
                        <Typography variant="body2" fontWeight="600" color="#475569" sx={{ fontSize: "0.72rem" }}>
                          {formatNumber(row.total_qty_sold)}
                        </Typography>
                      </Box>

                      <Box width="30%" textAlign="right">
                        <Typography variant="body2" fontWeight="700" color="#1e293b" sx={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}>
                          {formatCurrency(row.total_revenue)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Card>
          </Grid>

          {/* Sales Distribution Chart */}
          <Grid item xs={12} lg={3}>
            <Card elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: "1px solid #e2e8f0", backgroundColor: "#fff", height: 360, display: "flex", flexDirection: "column", justifyContent: "space-between", overflow: "visible", transition: "box-shadow 0.2s ease, border-color 0.2s ease", "&:hover": { boxShadow: "0 6px 24px -4px rgba(15,23,42,0.08)", borderColor: "#cbd5e1" }, animation: mounted ? "dashFadeSlideUp 0.45s ease-out 220ms both" : "none" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle1" fontWeight="700" color="#0f172a">
                  Store Sales Distribution
                </Typography>
                <Chip label="Stores" size="small" variant="outlined" color="primary" />
              </Box>

              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={250}>
                  <ShinySkeleton width={180} height={180} borderRadius="50%" />
                </Box>
              ) : salesDistributionList.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={260}>
                  <Typography variant="body2" color="text.secondary">
                    No store sales data recorded for selected filters.
                  </Typography>
                </Box>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1} mt={1}>
                  <Chart
                    options={{
                      chart: {
                        type: "pie",
                        animations: { enabled: true, easing: "easeinout", speed: 650 },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const idx = config?.dataPointIndex;
                            if (idx !== undefined && idx >= 0) {
                              const storeObj = salesDistributionList[idx];
                              const storeName = distLabels[idx] || "Store";
                              const targetStoreId = findStoreId(storeObj, storeName);

                              const filterList = [];
                              if (targetStoreId) {
                                filterList.push({ id: "store_id", value: targetStoreId });
                              }
                              if (fromDate) {
                                filterList.push({ id: "order_date_from", value: fromDate });
                              }
                              if (toDate) {
                                filterList.push({ id: "order_date_to", value: toDate });
                              }

                              setTimeout(() => {
                                navigate("/order/list", {
                                  state: {
                                    filters: filterList,
                                    storeId: targetStoreId,
                                    storeName: storeName,
                                    fromDate: fromDate || undefined,
                                    toDate: toDate || undefined,
                                    period: period,
                                    reportTitle: `Store Sales: ${storeName}`,
                                  },
                                });
                              }, 60);
                            }
                          },
                          legendClick: (chartContext, seriesIndex) => {
                            if (seriesIndex !== undefined && seriesIndex >= 0) {
                              const storeObj = salesDistributionList[seriesIndex];
                              const storeName = distLabels[seriesIndex] || "Store";
                              const targetStoreId = findStoreId(storeObj, storeName);

                              const filterList = [];
                              if (targetStoreId) {
                                filterList.push({ id: "store_id", value: targetStoreId });
                              }
                              if (fromDate) {
                                filterList.push({ id: "order_date_from", value: fromDate });
                              }
                              if (toDate) {
                                filterList.push({ id: "order_date_to", value: toDate });
                              }

                              setTimeout(() => {
                                navigate("/order/list", {
                                  state: {
                                    filters: filterList,
                                    storeId: targetStoreId,
                                    storeName: storeName,
                                    fromDate: fromDate || undefined,
                                    toDate: toDate || undefined,
                                    period: period,
                                    reportTitle: `Store Sales: ${storeName}`,
                                  },
                                });
                              }, 60);
                              return false;
                            }
                          },
                        },
                      },
                      labels: distLabels,
                      colors: ["#2563eb", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4", "#64748b"],
                      legend: {
                        position: "bottom",
                        fontSize: "11px",
                        fontWeight: 500,
                        horizontalAlign: "center",
                        formatter: (seriesName, opts) => {
                          const val = opts.w.globals.series[opts.seriesIndex] || 0;
                          return `${seriesName}: ${formatCurrency(val)}`;
                        },
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: (val) => `${val.toFixed(0)}%`,
                        style: { fontSize: "11px", fontWeight: "700" },
                        dropShadow: { enabled: false },
                      },
                      tooltip: {
                        enabled: true,
                        theme: "dark",
                        followCursor: true,
                        fillSeriesColor: false,
                        y: { formatter: (val) => formatCurrency(val) },
                      },
                    }}
                    series={distValues}
                    type="pie"
                    height={260}
                  />
                </Box>
              )}
            </Card>
          </Grid>
        </Grid>

        {/* 3. MIDDLE ROW 2: RECENT ORDERS + INVENTORY ALERTS + COLLECTIONS OVERVIEW */}
        <Grid container spacing={2.5} mb={3}>
          {/* Recent Orders */}
          <Grid item xs={12} lg={7} sx={{ display: "flex", flexDirection: "column" }}>
            <Card elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: "1px solid #e2e8f0", backgroundColor: "#fff", height: "100%", minHeight: 385, display: "flex", flexDirection: "column", transition: "box-shadow 0.2s ease, border-color 0.2s ease", "&:hover": { boxShadow: "0 6px 24px -4px rgba(15,23,42,0.08)", borderColor: "#cbd5e1" }, animation: mounted ? "dashFadeSlideUp 0.45s ease-out 80ms both" : "none" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight="700" color="#1e293b" sx={{ fontSize: "0.95rem" }}>
                  Recent Orders
                </Typography>
                {hasPermission(2) && (
                  <Typography
                    variant="caption"
                    fontWeight="700"
                    color="#2563eb"
                    sx={{ cursor: "pointer", fontSize: "0.72rem", "&:hover": { textDecoration: "underline" } }}
                    onClick={() => navigate("/order/list", { state: { ...getCardFilterState("recent_orders"), reportTitle: "Recent Orders Report" } })}
                  >
                    View All
                  </Typography>
                )}
              </Box>

              {/* Scrollable Table Container */}
              <Box sx={{ overflow: "auto", flexGrow: 1 }}>
                <Box sx={{ minWidth: 900 }}>
                  {/* Sub-header row */}
                  <Box display="flex" alignItems="center" pb={1} mb={1} sx={{ borderBottom: "1px solid #f1f5f9", px: 0.5 }}>
                    <Box sx={{ width: 120, flexShrink: 0 }}>
                      <Typography variant="caption" fontWeight="600" color="#64748b" sx={{ fontSize: "0.70rem", whiteSpace: "nowrap" }}>
                        Invoice No.
                      </Typography>
                    </Box>
                    <Box sx={{ width: 110, flexShrink: 0 }}>
                      <Typography variant="caption" fontWeight="600" color="#64748b" sx={{ fontSize: "0.70rem", whiteSpace: "nowrap" }}>
                        Date
                      </Typography>
                    </Box>
                    <Box sx={{ width: 175, flexShrink: 0, px: 0.5 }}>
                      <Typography variant="caption" fontWeight="600" color="#64748b" sx={{ fontSize: "0.70rem", whiteSpace: "nowrap" }}>
                        Customer / Company
                      </Typography>
                    </Box>
                    <Box sx={{ width: 145, flexShrink: 0, px: 0.5 }}>
                      <Typography variant="caption" fontWeight="600" color="#64748b" sx={{ fontSize: "0.70rem", whiteSpace: "nowrap" }}>
                        Payment Mode
                      </Typography>
                    </Box>
                    <Box sx={{ width: 180, flexShrink: 0, px: 0.5 }}>
                      <Typography variant="caption" fontWeight="600" color="#64748b" sx={{ fontSize: "0.70rem", whiteSpace: "nowrap" }}>
                        Salesperson
                      </Typography>
                    </Box>
                    <Box sx={{ width: 90, flexShrink: 0, textAlign: "right" }}>
                      <Typography variant="caption" fontWeight="600" color="#64748b" sx={{ fontSize: "0.70rem", whiteSpace: "nowrap" }}>
                        Amount
                      </Typography>
                    </Box>
                    <Box sx={{ width: 80, flexShrink: 0, textAlign: "right" }}>
                      <Typography variant="caption" fontWeight="600" color="#64748b" sx={{ fontSize: "0.70rem", whiteSpace: "nowrap" }}>
                        Status
                      </Typography>
                    </Box>
                  </Box>

                  {loading ? (
                    <Box display="flex" flexDirection="column" gap={1.5} py={1}>
                      {[1, 2, 3, 4, 5].map((i) => (
                        <ShinySkeleton key={i} height={32} borderRadius={6} />
                      ))}
                    </Box>
                  ) : recentOrdersList.length === 0 ? (
                    <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                      <Typography variant="body2" color="text.secondary">
                        No orders recorded.
                      </Typography>
                    </Box>
                  ) : (
                    <Box display="flex" flexDirection="column" gap={1}>
                      {recentOrdersList.map((row, idx) => {
                        let chipBg = "#dcfce7"; let chipColor = "#15803d";
                        if (row.status === "Packed" || row.status === "Approved") { chipBg = "#dbeafe"; chipColor = "#1d4ed8"; }
                        if (row.status === "Processing" || row.status === "Pending" || String(row.status).toLowerCase().includes("partially")) { chipBg = "#ffedd5"; chipColor = "#c2410c"; }

                        return (
                          <Box
                            key={row.order_id || idx}
                            display="flex"
                            alignItems="center"
                            py={0.4}
                            sx={{
                              "&:hover": { backgroundColor: "#f8fafc" },
                              borderRadius: 1.5,
                              px: 0.5,
                              transition: "background-color 0.18s ease",
                              animation: mounted ? `dashFadeSlideUp 0.28s ease-out ${Math.min(idx, 7) * 38}ms both` : "none",
                            }}
                          >
                            <Box sx={{ width: 120, flexShrink: 0 }}>
                              <Typography
                                variant="caption"
                                fontWeight="700"
                                color="#2563eb"
                                sx={{ cursor: "pointer", fontSize: "0.72rem", "&:hover": { textDecoration: "underline" }, whiteSpace: "nowrap" }}
                                onClick={() =>
                                  navigate("/order/list", {
                                    state: {
                                      filters: [{ id: "order_number", value: row.order_no }],
                                    },
                                  })
                                }
                              >
                                {row.order_no}
                              </Typography>
                            </Box>
                            <Box sx={{ width: 110, flexShrink: 0 }}>
                              <Typography variant="body2" color="#64748b" fontWeight="500" sx={{ fontSize: "0.70rem", whiteSpace: "nowrap" }}>
                                {row.order_date_formatted}
                              </Typography>
                            </Box>
                            <Box sx={{ width: 175, flexShrink: 0, px: 0.5 }}>
                              <Tooltip title={row.customer_name || ""} arrow placement="top">
                                <Typography
                                  variant="body2"
                                  fontWeight="600"
                                  color="#1e293b"
                                  sx={{
                                    fontSize: "0.72rem",
                                    lineHeight: 1.15,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {row.customer_name}
                                </Typography>
                              </Tooltip>
                            </Box>
                            <Box sx={{ width: 145, flexShrink: 0, px: 0.5 }}>
                              <Tooltip title={row.payment_mode || "N/A"} arrow placement="top">
                                <Typography
                                  variant="body2"
                                  fontWeight="600"
                                  color="#475569"
                                  sx={{
                                    fontSize: "0.70rem",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {row.payment_mode || "N/A"}
                                </Typography>
                              </Tooltip>
                            </Box>
                            <Box sx={{ width: 180, flexShrink: 0, px: 0.5 }}>
                              <Tooltip title={row.salesman_name || "N/A"} arrow placement="top">
                                <Typography
                                  variant="body2"
                                  fontWeight="600"
                                  color="#475569"
                                  sx={{
                                    fontSize: "0.70rem",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {row.salesman_name || "N/A"}
                                </Typography>
                              </Tooltip>
                            </Box>
                            <Box sx={{ width: 90, flexShrink: 0, textAlign: "right" }}>
                              <Typography variant="body2" fontWeight="700" color="#1e293b" sx={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}>
                                {formatCurrency(row.amount)}
                              </Typography>
                            </Box>
                            <Box sx={{ width: 80, flexShrink: 0, textAlign: "right", display: "flex", justifyContent: "flex-end" }}>
                              <Box
                                sx={{
                                  backgroundColor: chipBg,
                                  color: chipColor,
                                  fontWeight: "700",
                                  fontSize: "0.62rem",
                                  px: 0.8,
                                  py: 0.2,
                                  borderRadius: 3,
                                  whiteSpace: "nowrap",
                                  display: "inline-block",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {row.status}
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </Box>
            </Card>
          </Grid>

          {/* Collections Overview */}
          <Grid item xs={12} lg={5} sx={{ display: "flex", flexDirection: "column" }}>
            <Card elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: "1px solid #e2e8f0", backgroundColor: "#fff", height: "100%", minHeight: 385, display: "flex", flexDirection: "column", justifyContent: "space-between", transition: "box-shadow 0.2s ease, border-color 0.2s ease", "&:hover": { boxShadow: "0 6px 24px -4px rgba(15,23,42,0.08)", borderColor: "#cbd5e1" }, animation: mounted ? "dashFadeSlideUp 0.45s ease-out 240ms both" : "none" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle1" fontWeight="700" color="#0f172a">Collections Overview</Typography>
                {hasPermission(7) && (
                  <Typography
                    variant="caption"
                    fontWeight="700"
                    color="#2563eb"
                    sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                    onClick={() => navigate("/reports/sales-report", { state: { ...getCardFilterState("sales_report"), reportTitle: "Sales Report" } })}
                  >
                    View All
                  </Typography>
                )}
              </Box>

              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={240}>
                  <ShinySkeleton width={170} height={170} borderRadius="50%" />
                </Box>
              ) : (
                <Box display="flex" flexDirection="column" gap={1}>
                  {/* Top Donut Chart */}
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height={195}
                    sx={{
                      position: "relative",
                      overflow: "visible",
                      "& .apexcharts-tooltip": {
                        zIndex: "9999 !important",
                        boxShadow: "0 10px 25px -5px rgba(0,0,0,0.25) !important",
                        pointerEvents: "none",
                      },
                    }}
                  >
                    <Chart
                      options={{
                        chart: {
                          type: "donut",
                          animations: { enabled: true, easing: "easeinout", speed: 650 },
                          events: {
                            dataPointSelection: (event, chartContext, config) => {
                              const idx = config?.dataPointIndex;
                              if (idx !== undefined && idx >= 0) {
                                const collectionsFilterStates = [
                                  { paymentStatus: "received", reportTitle: "Collections Received Report" },
                                  { paymentStatus: "pending", reportTitle: "Pending Collections Report" },
                                ];

                                const targetState = collectionsFilterStates[idx];
                                if (targetState) {
                                  setTimeout(() => {
                                    navigate("/order/list", { state: targetState });
                                  }, 60);
                                }
                              }
                            },
                          },
                        },
                        labels: ["Collected", "Outstanding"],
                        colors: ["#16a34a", "#f59e0b"],
                        legend: { show: false },
                        dataLabels: { enabled: false },
                        tooltip: {
                          enabled: true,
                          theme: "dark",
                          followCursor: true,
                          fillSeriesColor: false,
                          y: { formatter: (val) => formatCurrency(val) },
                        },
                        plotOptions: {
                          pie: {
                            expandOnClick: false,
                            donut: {
                              size: "68%",
                              labels: {
                                show: true,
                                name: {
                                  show: true,
                                  fontSize: "11px",
                                  color: "#64748b",
                                },
                                value: {
                                  show: true,
                                  fontSize: "14px",
                                  fontWeight: 700,
                                  color: "#0f172a",
                                  formatter: (val) => formatCurrency(val),
                                },
                                total: {
                                  show: true,
                                  label: "Collected",
                                  fontSize: "11px",
                                  fontWeight: 600,
                                  color: "#64748b",
                                  formatter: (w) => {
                                    const [rec = 0, pend = 0] = w.globals.seriesTotals || [];
                                    const tot = rec + pend;
                                    return tot > 0 ? `${Math.round((rec / tot) * 100)}%` : "—";
                                  },
                                },
                              },
                            },
                          },
                        },
                      }}
                      series={[
                        parseFloat(summary.collections_received || 0),
                        parseFloat(summary.pending_collections || 0),
                      ]}
                      type="donut"
                      height={195}
                    />
                  </Box>

                  {/* Bottom Legend Breakdown */}
                  <Box display="flex" flexDirection="column" gap={0.6}>
                    {(() => {
                      const received = parseFloat(summary.collections_received || 0);
                      const pendingColl = parseFloat(summary.pending_collections || 0);
                      const total = received + pendingColl;
                      const pctOf = (v) => (total > 0 ? `${Math.round((v / total) * 100)}%` : "0%");
                      const recToday = parseFloat(summary.received_today || 0);
                      const recWeek = parseFloat(summary.received_this_week || 0);
                      const recMonth = parseFloat(summary.received_this_month || 0);

                      const todayStr = moment().format("YYYY-MM-DD");
                      const weekStartStr = moment().subtract(7, "days").format("YYYY-MM-DD");
                      const monthStartStr = moment().startOf("month").format("YYYY-MM-DD");

                      const mainRows = [
                        { label: "Collected", val: formatCurrency(received), color: "#16a34a", pct: pctOf(received), filterState: { paymentStatus: "received", reportTitle: "Collections Received Report" } },
                        { label: "Outstanding", val: formatCurrency(pendingColl), color: "#f59e0b", pct: pctOf(pendingColl), filterState: { paymentStatus: "pending", reportTitle: "Pending Collections Report" } },
                      ];
                      // Not extra slices: these are already inside "Collected" above.
                      const recentRows = [
                        { label: "Today", val: formatCurrency(recToday), filterState: { paymentStatus: "received", fromDate: todayStr, toDate: todayStr, reportTitle: "Collections Received Today Report" } },
                        { label: "Last 7 days", val: formatCurrency(recWeek), filterState: { paymentStatus: "received", fromDate: weekStartStr, toDate: todayStr, reportTitle: "Collections Received Last 7 Days Report" } },
                        { label: "This month", val: formatCurrency(recMonth), filterState: { paymentStatus: "received", fromDate: monthStartStr, toDate: todayStr, reportTitle: "Collections Received This Month Report" } },
                      ];
                      const rowSx = { cursor: "pointer", p: 0.5, borderRadius: 1, transition: "background-color 0.15s ease", "&:hover": { backgroundColor: "#f8fafc" } };

                      return (
                        <>
                          {mainRows.map((item) => (
                            <Box key={item.label} display="flex" justifyContent="space-between" alignItems="center" onClick={() => navigate("/order/list", { state: item.filterState })} sx={rowSx}>
                              <Box display="flex" alignItems="center" gap={0.8}>
                                <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: item.color }} />
                                <Typography variant="caption" color="#475569" fontWeight="600" sx={{ fontSize: "0.68rem" }}>{item.label}</Typography>
                              </Box>
                              <Typography variant="caption" color="#0f172a" fontWeight="700" sx={{ fontSize: "0.68rem" }}>
                                {item.val} <span style={{ color: "#64748b", fontWeight: "600", marginLeft: 4 }}>{item.pct}</span>
                              </Typography>
                            </Box>
                          ))}
                          <Typography variant="caption" color="#94a3b8" fontWeight="600" sx={{ fontSize: "0.64rem", mt: 0.4, px: 0.5 }}>
                            Of the collected amount, received:
                          </Typography>
                          {recentRows.map((item) => (
                            <Box key={item.label} display="flex" justifyContent="space-between" alignItems="center" onClick={() => navigate("/order/list", { state: item.filterState })} sx={{ ...rowSx, py: 0.2, pl: 1.6 }}>
                              <Typography variant="caption" color="#64748b" fontWeight="600" sx={{ fontSize: "0.66rem" }}>{item.label}</Typography>
                              <Typography variant="caption" color="#334155" fontWeight="700" sx={{ fontSize: "0.66rem" }}>{item.val}</Typography>
                            </Box>
                          ))}
                        </>
                      );
                    })()}
                  </Box>
                </Box>
              )}
            </Card>
          </Grid>
          {!searching && (
          <>
          {/* Inventory Alerts */}
          <Grid item xs={12} lg={12}>
            <Card elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: "1px solid #e2e8f0", backgroundColor: "#fff", minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "flex-start", transition: "box-shadow 0.2s ease, border-color 0.2s ease", "&:hover": { boxShadow: "0 6px 24px -4px rgba(15,23,42,0.08)", borderColor: "#cbd5e1" }, animation: mounted ? "dashFadeSlideUp 0.45s ease-out 160ms both" : "none" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight="700" color="#0f172a">Inventory Alerts</Typography>
                {hasPermission(7) && (
                  <Typography
                    variant="caption"
                    fontWeight="700"
                    color="#2563eb"
                    sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                    onClick={() => navigate("/reports/stock-report", { state: { ...getCardFilterState("stock_report"), view: "product" } })}
                  >
                    View All
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, gap: 3, alignItems: "stretch" }}>
              <Box sx={{ width: { xs: "100%", lg: "30%" }, flexShrink: 0, display: "flex" }}>
              {/* Stock health — what share of the stock can actually be sold */}
              {(() => {
                const total = Number(inventoryAlerts.total_instock || 0);
                const reserved = Number(inventoryAlerts.reserved_stock || 0);
                const damaged = Number(inventoryAlerts.damaged_stock || 0);
                const expiredUnits = Number(inventoryAlerts.expired_units || 0);
                const available = Number(inventoryAlerts.available_stock || 0);
                const segs = [
                  { label: "Available units", value: available, color: "#16a34a" },
                  { label: "Reserved units", value: reserved, color: "#f59e0b" },
                  { label: "Expired units", value: expiredUnits, color: "#be123c" },
                ]; // these three add up to the units in stock; damaged units are a separate bucket
                const sum = segs.reduce((a, s) => a + s.value, 0) || 1;
                return (
                  <Box sx={{ flex: 1, p: 1.8, borderRadius: 2, backgroundColor: "#f8fafc", border: "1px solid #eef2f7", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                    <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={0.8}>
                      <Box display="flex" alignItems="baseline" gap={0.6}>
                        {loading ? (
                          <ShinySkeleton width={60} height={24} borderRadius={6} />
                        ) : (
                          <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>
                            {available.toLocaleString()}
                          </Typography>
                        )}
                        <Typography variant="caption" color="#64748b" sx={{ fontSize: "0.68rem" }}>
                          units sellable of {total.toLocaleString()} in stock
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        fontWeight="700"
                        color="#2563eb"
                        sx={{ fontSize: "0.66rem", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                        onClick={() => navigate(Number(storeId) > 0 ? `/reports/stock-report/${storeId}` : "/reports/stock-report", { state: { view: "product" } })}
                      >
                        Report →
                      </Typography>
                    </Box>
                    <Box display="flex" sx={{ height: 8, borderRadius: 4, overflow: "hidden", backgroundColor: "#e2e8f0", gap: "2px" }}>
                      {segs.filter((s) => s.value > 0).map((s) => (
                        <Tooltip key={s.label} title={`${s.label}: ${s.value.toLocaleString()}`} arrow>
                          <Box sx={{ flex: s.value / sum, minWidth: 4, backgroundColor: s.color }} />
                        </Tooltip>
                      ))}
                    </Box>
                    <Box display="flex" flexWrap="wrap" columnGap={1.4} rowGap={0.3} mt={0.8}>
                      {segs.map((s) => (
                        <Box key={s.label} display="flex" alignItems="center" gap={0.5}>
                          <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: s.color }} />
                          <Typography variant="caption" color="#64748b" sx={{ fontSize: "0.62rem" }}>
                            {s.label} <b style={{ color: "#334155" }}>{s.value.toLocaleString()}</b>
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    {damaged > 0 && (
                      <Typography variant="caption" color="#64748b" sx={{ fontSize: "0.62rem", display: "block", mt: 0.7, pt: 0.7, borderTop: "1px dashed #e2e8f0" }}>
                        Plus <b style={{ color: "#334155" }}>{damaged.toLocaleString()}</b> damaged units held separately (not in stock)
                      </Typography>
                    )}
                  </Box>
                );
              })()}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
              {loading ? (
                <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={1}>
                  {[1, 2, 3, 4].map((i) => (
                    <ShinySkeleton key={i} height={64} borderRadius={10} />
                  ))}
                </Box>
              ) : (
                (() => {
                  const alertGroups = [
                    {
                      title: "Needs attention · products",
                      items: [
                        { label: "Out of stock", filterType: "out_of_stock", count: Number(inventoryAlerts.out_of_stock || 0), color: "#dc2626", bg: "#fef2f2", icon: <ErrorOutlineIcon sx={{ fontSize: 16 }} />, helperText: "Zero units available" },
                        { label: "Low stock", filterType: "low_stock", count: Number(inventoryAlerts.low_stock || 0), color: "#ea580c", bg: "#fff7ed", icon: <WarningAmberIcon sx={{ fontSize: 16 }} />, helperText: "1–10 units" },
                        { label: "Expired products", filterType: "expired_stock", count: Number(inventoryAlerts.expired_stock || 0), color: "#be123c", bg: "#fff1f2", icon: <ErrorOutlineIcon sx={{ fontSize: 16 }} />, helperText: `${Number(inventoryAlerts.expired_units || 0).toLocaleString()} units, cannot be sold` },
                        { label: "Expiring in 30 days", filterType: "expiring_soon", count: Number(inventoryAlerts.expiring_soon || 0), color: "#b45309", bg: "#fffbeb", icon: <AccessTimeIcon sx={{ fontSize: 16 }} />, helperText: "Sell or move soon" },
                        { label: "Below reorder level", filterType: "reorder_alert", count: Number(inventoryAlerts.reorder_alert || 0), color: "#a16207", bg: "#fefce8", icon: <WarningAmberIcon sx={{ fontSize: 16 }} />, helperText: "At or below level" },
                      ],
                    },
                    {
                      title: "Sales movement · products",
                      items: [
                        { label: "High demand", filterType: "high_demand", count: Number(inventoryAlerts.high_demand || 0), color: "#16a34a", bg: "#f0fdf4", icon: <TrendingUpIcon sx={{ fontSize: 16 }} />, helperText: "Selling fast" },
                        { label: "Slow moving", filterType: "slow_moving", count: Number(inventoryAlerts.slow_moving || 0), color: "#2563eb", bg: "#eff6ff", icon: <AccessTimeIcon sx={{ fontSize: 16 }} />, helperText: "In stock, no sales this period" },
                      ],
                    },
                  ];
                  const openAlert = (item) => {
                    if (!hasPermission(7)) return;
                    navigate("/reports/stock-report", {
                      state: {
                        reportTitle: `Inventory Alert — ${item.label}`,
                        alertFilter: item.filterType,
                        fromDate,
                        toDate,
                        period,
                        storeId,
                        userId: userData?.emp_id,
                      },
                    });
                  };
                  return (
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, gap: { xs: 2, lg: 4 } }}>
                      {alertGroups.map((group, gi) => (
                        <Box key={group.title} sx={{ flex: gi === 0 ? 1.15 : 1, minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            fontWeight="700"
                            color="#94a3b8"
                            sx={{ fontSize: "0.62rem", letterSpacing: "0.07em", textTransform: "uppercase", display: "block", pb: 0.8, borderBottom: "1px solid #e2e8f0" }}
                          >
                            {group.title}
                          </Typography>
                          {group.items.map((item) => {
                            const active = item.count > 0;
                            return (
                              <Box
                                key={item.filterType}
                                onClick={() => openAlert(item)}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.2,
                                  py: 0.85,
                                  px: 0.5,
                                  borderBottom: "1px solid #f1f5f9",
                                  cursor: hasPermission(7) ? "pointer" : "default",
                                  borderRadius: 1,
                                  transition: "background-color 0.15s ease",
                                  "&:hover": hasPermission(7) ? { backgroundColor: "#f8fafc" } : {},
                                  "&:last-of-type": { borderBottom: "none" },
                                }}
                              >
                                <Box sx={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, backgroundColor: active ? item.color : "#cbd5e1" }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography noWrap sx={{ fontSize: "0.78rem", fontWeight: 600, color: active ? "#1e293b" : "#94a3b8", lineHeight: 1.25 }}>
                                    {item.label}
                                  </Typography>
                                  <Typography noWrap sx={{ fontSize: "0.64rem", color: "#94a3b8", lineHeight: 1.25 }}>
                                    {item.helperText}
                                  </Typography>
                                </Box>
                                <Typography
                                  sx={{
                                    fontSize: "0.82rem",
                                    fontWeight: 800,
                                    color: active ? item.color : "#94a3b8",
                                    backgroundColor: active ? item.bg : "transparent",
                                    borderRadius: 1.5,
                                    px: 1,
                                    minWidth: 38,
                                    textAlign: "center",
                                    lineHeight: 1.7,
                                  }}
                                >
                                  {item.count}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      ))}
                    </Box>
                  );
                })()
              )}
              </Box>
              </Box>
            </Card>
          </Grid>

          </>
          )}
        </Grid>

        {/* 4. BOTTOM ROW: TOP CUSTOMERS + UAE SALES DISTRIBUTION */}
        <Grid container spacing={2.5}>
          {/* Top Customers */}
          <Grid item xs={12} lg={6} sx={{ display: "flex", flexDirection: "column" }}>
            <Card elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: "1px solid #e2e8f0", backgroundColor: "#fff", height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", transition: "box-shadow 0.2s ease, border-color 0.2s ease", "&:hover": { boxShadow: "0 6px 24px -4px rgba(15,23,42,0.08)", borderColor: "#cbd5e1" }, animation: mounted ? "dashFadeSlideUp 0.45s ease-out 80ms both" : "none" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight="700" color="#1e293b" sx={{ fontSize: "0.95rem" }}>Top Customers / Companies</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Select
                    size="small"
                    value={customerSort}
                    onChange={(e) => handleCustomerSortChange(e.target.value)}
                    sx={{
                      fontSize: "0.72rem",
                      height: 28,
                      backgroundColor: "#fff",
                      borderRadius: 1.5,
                      border: "1px solid #e2e8f0",
                      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    }}
                  >
                    <MenuItem value="top">High</MenuItem>
                    <MenuItem value="bottom">Low</MenuItem>
                  </Select>
                  {hasPermission(4) && (
                    <Typography
                      variant="caption"
                      fontWeight="700"
                      color="#2563eb"
                      sx={{ cursor: "pointer", fontSize: "0.72rem", "&:hover": { textDecoration: "underline" } }}
                      onClick={() => navigate("/customer/list", { state: { ...getCardFilterState("customers"), customerScope: "ordered", reportTitle: "Customers — placed an invoice in the period" } })}
                    >
                      View All
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Sub-header row */}
              <Box display="flex" alignItems="center" pb={1} mb={1} sx={{ borderBottom: "1px solid #f1f5f9" }}>
                <Box width="45%">
                  <Typography variant="caption" fontWeight="600" color="#64748b" sx={{ fontSize: "0.70rem" }}>
                    Customer / Company
                  </Typography>
                </Box>
                <Box width="15%" textAlign="center">
                  <Typography variant="caption" fontWeight="600" color="#64748b" sx={{ fontSize: "0.70rem" }}>
                    Orders
                  </Typography>
                </Box>
                <Box width="20%" textAlign="right">
                  <Typography variant="caption" fontWeight="600" color="#64748b" sx={{ fontSize: "0.70rem" }}>
                    Revenue (AED)
                  </Typography>
                </Box>
                <Box width="20%" textAlign="right">
                  <Typography variant="caption" fontWeight="600" color="#64748b" sx={{ fontSize: "0.70rem" }}>
                    Last Purchase
                  </Typography>
                </Box>
              </Box>

              {customersLoading ? (
                <Box display="flex" flexDirection="column" gap={1.2} py={1}>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <ShinySkeleton key={i} height={28} borderRadius={6} />
                  ))}
                </Box>
              ) : topCustomersList.length === 0 ? (
                <Box py={4} textAlign="center">
                  <Typography variant="body2" color="text.secondary">
                    No customer / company sales records found.
                  </Typography>
                </Box>
              ) : (
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={0.6}
                  sx={{
                    maxHeight: 230,
                    overflowY: "auto",
                    pr: 0.5,
                    "&::-webkit-scrollbar": { width: 4 },
                    "&::-webkit-scrollbar-thumb": { backgroundColor: "#cbd5e1", borderRadius: 2 },
                  }}
                >
                  {topCustomersList.slice(0, 6).map((row, idx) => (
                    <Box
                      key={idx}
                      display="flex"
                      alignItems="center"
                      py={0.35}
                      onClick={() => {
                        if (!(Number(row.customer_id) > 0)) return;
                        navigate("/order/list", {
                          state: {
                            ...getCardFilterState("top_customers"),
                            customerId: row.customer_id,
                            reportTitle: `Invoices — ${row.customer_name}`,
                          },
                        });
                      }}
                      sx={{
                        cursor: Number(row.customer_id) > 0 ? "pointer" : "default",
                        "&:hover": { backgroundColor: "#f1f5f9" },
                        borderRadius: 1.5,
                        px: 0.5,
                        transition: "background-color 0.18s ease",
                        animation: mounted ? `dashFadeSlideUp 0.28s ease-out ${Math.min(idx, 5) * 45}ms both` : "none",
                      }}
                    >
                      <Box width="45%">
                        <Tooltip title={row.customer_name || ""} arrow placement="top">
                          <Typography
                            variant="body2"
                            fontWeight="600"
                            color="#1e293b"
                            sx={{
                              fontSize: "0.76rem",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              cursor: "pointer",
                            }}
                          >
                            {row.customer_name}
                          </Typography>
                        </Tooltip>
                      </Box>
                      <Box width="15%" textAlign="center">
                        <Typography variant="body2" fontWeight="600" color="#475569" sx={{ fontSize: "0.76rem" }}>
                          {row.orders}
                        </Typography>
                      </Box>
                      <Box width="20%" textAlign="right">
                        <Tooltip title={formatCurrency(row.revenue)} arrow placement="top">
                          <Typography
                            variant="body2"
                            fontWeight="700"
                            color="#1e293b"
                            sx={{
                              fontSize: "0.76rem",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              cursor: "pointer",
                            }}
                          >
                            {formatCurrency(row.revenue)}
                          </Typography>
                        </Tooltip>
                      </Box>
                      <Box width="20%" textAlign="right">
                        <Typography variant="body2" color="#64748b" fontWeight="500" sx={{ fontSize: "0.74rem" }}>
                          {row.last_purchase}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Card>
          </Grid>

          {/* UAE Sales Distribution Map (Placed on the right of Top Customers) */}
          <Grid item xs={12} lg={6} sx={{ display: "flex", flexDirection: "column" }}>
            <UAESalesMap
              data={dashboardData.uaeSalesDistribution || []}
              totalSales={parseFloat(summary.monthly_sales || summary.total_revenue || 0)}
              loading={loading}
            />
          </Grid>
        </Grid>

        {/* 5. ADDITIONAL ROW: CATEGORY SALES + SALES BY CHANNEL + EXECUTIVE PERFORMANCE */}
        <Grid container spacing={2.5} sx={{ mt: 1 }}>
          {/* Product Category Sales Chart */}
          <Grid item xs={12} lg={4} sx={{ display: "flex", flexDirection: "column" }}>
            <Card elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: "1px solid #e2e8f0", backgroundColor: "#fff", height: "100%", transition: "box-shadow 0.2s ease, border-color 0.2s ease", "&:hover": { boxShadow: "0 6px 24px -4px rgba(15,23,42,0.08)", borderColor: "#cbd5e1" }, animation: mounted ? "dashFadeSlideUp 0.45s ease-out 80ms both" : "none" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle1" fontWeight="700" color="#0f172a">
                  Product Category Sales
                </Typography>
                <Chip label="By Category" size="small" variant="outlined" color="primary" />
              </Box>
              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={260}>
                  <ShinySkeleton width={180} height={180} borderRadius="50%" />
                </Box>
              ) : (
                <Box mt={1}>
                  {(() => {
                    const catList = categorySalesList;
                    if (catList.length === 0) {
                      return (
                        <Box p={4} textAlign="center">
                          <Typography variant="body2" color="text.secondary">
                            No category sales records found.
                          </Typography>
                        </Box>
                      );
                    }

                    // Limit to top 5, group rest as "Others"
                    const top5 = catList.slice(0, 5);
                    const othersRevenue = catList.slice(5).reduce((acc, i) => acc + parseFloat(i.category_revenue || 0), 0);
                    const displayCats = othersRevenue > 0
                      ? [...top5, { category_name: "Others", category_revenue: othersRevenue }]
                      : top5;

                    const palette = ["#2563eb", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#94a3b8"];
                    const amounts = displayCats.map((i) => parseFloat(i.category_revenue || 0));
                    const totalAmt = amounts.reduce((x, y) => x + y, 0);
                    const maxAmt = Math.max(...amounts, 1);

                    const openCategory = (cat) => {
                      if (!hasPermission(3) || !cat) return;
                      if (cat.category_name !== "Others") {
                        // Sales Report (product-wise) for this category, with
                        // a TOTAL footer equal to the amount shown here.
                        navigate("/reports/sales-report", {
                          state: {
                            ...getCardFilterState("category_sales"),
                            category: cat.category_name,
                            reportTitle: `Category Sales: ${cat.category_name}`,
                          },
                        });
                      } else {
                        navigate("/product/list", { state: getCardFilterState("category_others") });
                      }
                    };

                    return (
                      <Box>
                        <Box display="flex" alignItems="baseline" justifyContent="space-between" mb={1.4}>
                          <Typography sx={{ fontSize: "1.3rem", fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>
                            {formatCurrency(totalAmt)}
                          </Typography>
                          <Typography variant="caption" color="#64748b" sx={{ fontSize: "0.68rem" }}>
                            across {catList.length} {catList.length === 1 ? "category" : "categories"}
                          </Typography>
                        </Box>
                        <Box display="flex" flexDirection="column" gap={1.5}>
                          {displayCats.map((cat, idx) => {
                            const amt = amounts[idx];
                            const share = totalAmt > 0 ? (amt / totalAmt) * 100 : 0;
                            const color = palette[idx % palette.length];
                            return (
                              <Box
                                key={`${cat.category_name}-${idx}`}
                                onClick={() => openCategory(cat)}
                                sx={{
                                  cursor: hasPermission(3) ? "pointer" : "default",
                                  borderRadius: 1.5,
                                  px: 0.75,
                                  py: 0.5,
                                  mx: -0.75,
                                  transition: "background-color 0.15s ease",
                                  "&:hover": hasPermission(3) ? { backgroundColor: "#f8fafc" } : {},
                                }}
                              >
                                <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                                  <Box display="flex" alignItems="center" gap={0.8} minWidth={0}>
                                    <Box sx={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: color, flexShrink: 0 }} />
                                    <Tooltip title={cat.category_name} arrow placement="top">
                                      <Typography
                                        variant="body2"
                                        fontWeight="600"
                                        color="#1e293b"
                                        sx={{ fontSize: "0.78rem", lineHeight: 1.25, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", wordBreak: "break-word" }}
                                      >
                                        {cat.category_name}
                                      </Typography>
                                    </Tooltip>
                                  </Box>
                                  <Box textAlign="right" flexShrink={0}>
                                    <Typography variant="body2" fontWeight="700" color="#0f172a" sx={{ fontSize: "0.78rem", lineHeight: 1.2 }}>
                                      {formatCurrency(amt)}
                                    </Typography>
                                    <Typography variant="caption" color="#64748b" sx={{ fontSize: "0.64rem" }}>
                                      {share.toFixed(share < 10 && share > 0 ? 1 : 0)}%
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ mt: 0.6, height: 6, borderRadius: 3, backgroundColor: "#f1f5f9", overflow: "hidden" }}>
                                  <Box sx={{ width: `${Math.max((amt / maxAmt) * 100, amt > 0 ? 3 : 0)}%`, height: "100%", borderRadius: 3, backgroundColor: color, transition: "width 0.5s ease" }} />
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })()}
                </Box>
              )}
            </Card>
          </Grid>

          {/* ("Sales by Store" removed — duplicated the Store Sales Distribution chart.) */}
          {!searching && (
          <>
          {/* Executive Performance (This Month) */}
          <Grid item xs={12} lg={8} sx={{ display: "flex", flexDirection: "column" }}>
            <Card elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: "1px solid #e2e8f0", backgroundColor: "#fff", height: "100%", transition: "box-shadow 0.2s ease, border-color 0.2s ease", "&:hover": { boxShadow: "0 6px 24px -4px rgba(15,23,42,0.08)", borderColor: "#cbd5e1" }, animation: mounted ? "dashFadeSlideUp 0.45s ease-out 200ms both" : "none" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight="700" color="#0f172a">Executive Performance</Typography>
                <Select
                  size="small"
                  value={execPerfPeriod}
                  onChange={(e) => handleExecPerfPeriodChange(e.target.value)}
                  sx={{
                    fontSize: "0.72rem",
                    height: 28,
                    backgroundColor: "#fff",
                    borderRadius: 1.5,
                    border: "1px solid #e2e8f0",
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  }}
                >
                  <MenuItem value="all">All Time</MenuItem>
                  <MenuItem value="daily">Today</MenuItem>
                  <MenuItem value="weekly">Last 7 Days</MenuItem>
                  <MenuItem value="monthly">This Month</MenuItem>
                  <MenuItem value="yearly">This Year</MenuItem>
                </Select>
              </Box>
              {execPerfLoading ? (
                <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={1.5} minHeight={200}>
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <ShinySkeleton key={i} height={60} borderRadius={8} />
                  ))}
                </Box>
              ) : (
                <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={1.5}>
                  {(() => {
                    const perfSum = execPerfSummary || summary;
                    const perfOrders = perfSum.period_orders !== undefined ? perfSum.period_orders : (perfSum.monthly_orders !== undefined ? perfSum.monthly_orders : (perfSum.daily_sales || 0));
                    const perfRevenue = perfSum.period_sales !== undefined ? perfSum.period_sales : (perfSum.monthly_sales !== undefined ? perfSum.monthly_sales : (perfSum.total_revenue || 0));
                    const perfNewCust = perfSum.new_customers || 0;

                    // Plain-language comparison with the previous period, e.g.
                    // "▲ 25% more than yesterday (was 4)". Shows the real previous
                    // figure, so a jump from 0 isn't passed off as "+100%".
                    const prevWord = {
                      daily: "yesterday",
                      weekly: "the previous 7 days",
                      monthly: "last month",
                      yearly: "last year",
                    }[execPerfPeriod];
                    const perfChange = (curr, prev, fmt = (n) => String(n)) => {
                      if (!prevWord) return { change: "Total since the beginning", tone: "neutral" };
                      const c = Number(curr) || 0;
                      const p = Number(prev) || 0;
                      if (Math.abs(c - p) < 0.005) {
                        return { change: `No change from ${prevWord} (${fmt(p)})`, tone: "neutral" };
                      }
                      if (p === 0) return { change: `▲ New — none ${prevWord === "yesterday" ? "yesterday" : "in " + prevWord}`, tone: "up" };
                      const pct = Math.round((Math.abs(c - p) / p) * 100);
                      return c > p
                        ? { change: `▲ ${pct}% more than ${prevWord} (was ${fmt(p)})`, tone: "up" }
                        : { change: `▼ ${pct}% less than ${prevWord} (was ${fmt(p)})`, tone: "down" };
                    };

                    const perfOrdersGrowth = perfChange(perfOrders, perfSum.prev_period_orders);
                    const perfRevGrowth = perfChange(perfRevenue, perfSum.prev_period_sales, formatCurrency);
                    const perfNewCustGrowth = perfChange(perfNewCust, perfSum.prev_active_customers);
                    const visitsGrowth = perfChange(perfSum.customer_visits, perfSum.prev_customer_visits);
                    const callsGrowth = perfChange(perfSum.calls_made, perfSum.prev_calls_made);
                    const convGrowth =
                      perfSum.curr_conversion_pct == null
                        ? { change: "No calls or visits in this period", tone: "neutral" }
                        : perfSum.prev_conversion_pct == null
                          ? { change: prevWord ? `No calls or visits ${prevWord === "yesterday" ? "yesterday" : "in " + prevWord} to compare` : "Total since the beginning", tone: "neutral" }
                          : perfChange(perfSum.curr_conversion_pct, perfSum.prev_conversion_pct, (n) => `${n}%`);

                    const getExecPerfFilterState = (cardType) => {
                      const todayStr = moment().format("YYYY-MM-DD");
                      let efFrom = "";
                      let efTo = "";

                      if (execPerfPeriod === "daily") {
                        efFrom = todayStr;
                        efTo = todayStr;
                      } else if (execPerfPeriod === "weekly") {
                        efFrom = moment().subtract(7, "days").format("YYYY-MM-DD");
                        efTo = todayStr;
                      } else if (execPerfPeriod === "yearly") {
                        efFrom = moment().startOf("year").format("YYYY-MM-DD");
                        efTo = todayStr;
                      } else {
                        efFrom = moment().startOf("month").format("YYYY-MM-DD");
                        efTo = todayStr;
                      }

                      return {
                        fromDate: efFrom,
                        toDate: efTo,
                        storeId,
                        salesPersonId,
                        period: execPerfPeriod,
                        cardType,
                      };
                    };

                    return [
                      { label: "Orders Closed", val: perfOrders, change: perfOrdersGrowth.change, tone: perfOrdersGrowth.tone, path: "/order/list", filterState: { ...getExecPerfFilterState("orders_closed"), reportTitle: "Orders Closed Report" } },
                      { label: "Revenue Generated", val: formatCurrency(perfRevenue), change: perfRevGrowth.change, tone: perfRevGrowth.tone, path: "/reports/sales-report", filterState: { ...getExecPerfFilterState("revenue_generated"), reportTitle: "Revenue Generated Report" } },
                      { label: "Customers Who Ordered", val: perfNewCust, change: perfNewCustGrowth.change, tone: perfNewCustGrowth.tone, path: "/customer/list", filterState: { ...getExecPerfFilterState("new_customers"), reportTitle: "New Customers Report" } },
                      { label: "Customers Visited", val: perfSum.customer_visits || 0, change: visitsGrowth.change, tone: visitsGrowth.tone, path: "/sales-call-report/list", filterState: { ...getExecPerfFilterState("company_visits"), reportTitle: "Company Visits Report" } },
                      { label: "Calls Made", val: perfSum.calls_made || 0, change: callsGrowth.change, tone: callsGrowth.tone, path: "/sales-call-report/list", filterState: { ...getExecPerfFilterState("calls_made"), reportTitle: "Calls Made Report" } },
                      { label: "Conversion Rate", val: perfSum.conversion_rate || "N/A", change: convGrowth.change, tone: convGrowth.tone },
                    ].map((perf, idx) => (
                      <Box
                        key={idx}
                        p={1.2}
                        borderRadius={2}
                        onClick={() => perf.path && navigate(perf.path, { state: perf.filterState })}
                        sx={{
                          backgroundColor: "#f8fafc",
                          border: "1px solid #f1f5f9",
                          cursor: perf.path ? "pointer" : "default",
                          transition: "all 0.2s ease",
                          animation: mounted ? `dashFadeSlideUp 0.3s ease-out ${idx * 60}ms both` : "none",
                          "&:hover": perf.path ? { backgroundColor: "#f1f5f9", transform: "translateY(-2px)", boxShadow: "0 3px 8px rgba(0,0,0,0.06)" } : {},
                        }}
                      >
                        <Typography variant="caption" color="#64748b" fontWeight="600" display="block">{perf.label}</Typography>
                        <Typography variant="subtitle2" fontWeight="800" color="#0f172a" my={0.2} sx={{ fontSize: "0.85rem" }}>{perf.val}</Typography>
                        <Typography variant="caption" fontWeight="700" color={perf.tone === "up" ? "#16a34a" : perf.tone === "down" ? "#dc2626" : "#64748b"} sx={{ fontSize: "0.68rem" }}>{perf.change}</Typography>
                      </Box>
                    ));
                  })()}
                </Box>
              )}
            </Card>
          </Grid>
          </>
          )}
        </Grid>

        </>
        )}

        {!searching && (
        <>
        {/* Sales Target Widget */}
        <Box sx={{ mt: 8, mb: 2, clear: "both", width: "100%" }}>
          <SalesTargetWidget
            salesPersonId={salesPersonId || null}
            storeId={storeId || null}
            fromDate={fromDate || null}
            toDate={toDate || null}
            period={period || null}
          />
        </Box>

        </>
        )}

        {/* 6. DAMAGED PRODUCTS & LOSS REPORT WIDGET */}
        {isAdmin && !searching && (
          <Box sx={{ mt: 4, mb: 3 }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <Card
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2.5,
                    border: "1px solid #e2e8f0",
                    backgroundColor: "#fff",
                    transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                    "&:hover": { boxShadow: "0 6px 24px -4px rgba(15,23,42,0.08)", borderColor: "#cbd5e1" },
                    animation: mounted ? "dashFadeSlideUp 0.45s ease-out 100ms both" : "none",
                  }}
                >
                  {(() => {
                    const getDamagedNavState = (extraSearch = null, extraStoreId = null, extraStoreName = null) => {
                      const todayStr = moment().format("YYYY-MM-DD");
                      let dFrom = "";
                      let dTo = "";
                      if (damagedPeriod === "daily") {
                        dFrom = todayStr; dTo = todayStr;
                      } else if (damagedPeriod === "weekly") {
                        dFrom = moment().subtract(7, "days").format("YYYY-MM-DD"); dTo = todayStr;
                      } else if (damagedPeriod === "yearly") {
                        dFrom = moment().startOf("year").format("YYYY-MM-DD"); dTo = todayStr;
                      } else if (damagedPeriod === "monthly") {
                        dFrom = moment().startOf("month").format("YYYY-MM-DD"); dTo = todayStr;
                      } else {
                        dFrom = fromDate; dTo = toDate;
                      }

                      const selectedStoreId = extraStoreId || storeId;
                      return {
                        store_id: selectedStoreId,
                        storeId: selectedStoreId,
                        ...(extraStoreName && { store_name: extraStoreName }),
                        fromDate: dFrom,
                        toDate: dTo,
                        period: damagedPeriod,
                        ...(extraSearch && { search: extraSearch, globalSearch: extraSearch }),
                      };
                    };

                    return (
                      <>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Box
                              sx={{
                                p: 1,
                                borderRadius: 2,
                                backgroundColor: "#fef2f2",
                                color: "#dc2626",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <ReportProblemIcon />
                            </Box>
                            <Box>
                              <Typography variant="h6" fontWeight="700" color="#0f172a" sx={{ fontSize: "1.05rem" }}>
                                Damaged Products & Loss Report
                              </Typography>
                              <Typography variant="caption" color="#64748b" sx={{ fontSize: "0.72rem" }}>
                                Inventory damage metrics, financial impact & store breakdowns
                              </Typography>
                            </Box>
                          </Box>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Select
                              value={damagedPeriod}
                              onChange={(e) => handleDamagedPeriodChange(e.target.value)}
                              size="small"
                              sx={{
                                height: 28,
                                fontSize: "0.72rem",
                                fontWeight: 700,
                                color: "#dc2626",
                                backgroundColor: "#fef2f2",
                                borderRadius: 1.5,
                                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                                "& .MuiSelect-select": { py: 0.4, px: 1.2 },
                              }}
                            >
                              <MenuItem value="all">All Time</MenuItem>
                              <MenuItem value="daily">Today</MenuItem>
                              <MenuItem value="weekly">Weekly</MenuItem>
                              <MenuItem value="monthly">This Month</MenuItem>
                              <MenuItem value="yearly">Yearly</MenuItem>
                            </Select>
                            <Chip
                              label="Inventory Loss"
                              size="small"
                              variant="outlined"
                              color="error"
                              sx={{ fontWeight: 700, borderRadius: 1.5 }}
                            />
                            <Typography
                              variant="caption"
                              fontWeight="700"
                              color="#2563eb"
                              sx={{ cursor: "pointer", fontSize: "0.74rem", "&:hover": { textDecoration: "underline" } }}
                              onClick={() => navigate("/invoice/damaged-report", { state: getDamagedNavState() })}
                            >
                              View Full Report
                            </Typography>
                          </Box>
                        </Box>

                        {/* Summary KPI Cards Row */}
                        {isDamagedLoading ? (
                          <Grid container spacing={2} mb={3}>
                            {[1, 2, 3].map((i) => (
                              <Grid item xs={12} sm={4} key={i}>
                                <ShinySkeleton height={76} borderRadius={8} />
                              </Grid>
                            ))}
                          </Grid>
                        ) : (
                          <Grid container spacing={2} mb={3}>
                            <Grid item xs={12} sm={4}>
                              <Box
                                onClick={() => navigate("/invoice/damaged-report", { state: getDamagedNavState() })}
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  backgroundColor: "#fef2f2",
                                  border: "1px solid #fee2e2",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                  "&:hover": { transform: "translateY(-2px)", boxShadow: "0 4px 12px rgba(220,38,38,0.12)" },
                                }}
                              >
                                <Box>
                                  <Typography variant="caption" fontWeight="600" color="#991b1b" sx={{ fontSize: "0.72rem" }}>
                                    Total Damaged Qty
                                  </Typography>
                                  <Typography variant="h6" fontWeight="800" color="#dc2626" sx={{ mt: 0.3 }}>
                                    {damagedSummary.total_damaged_qty || 0}{" "}
                                    <Typography component="span" variant="caption" fontWeight="600" color="#b91c1c">
                                      units
                                    </Typography>
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    p: 1,
                                    borderRadius: 1.5,
                                    backgroundColor: "#fff",
                                    color: "#dc2626",
                                    boxShadow: "0 2px 4px rgba(220,38,38,0.08)",
                                  }}
                                >
                                  <WarningAmberIcon />
                                </Box>
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                              <Box
                                onClick={() => navigate("/invoice/damaged-report", { state: getDamagedNavState() })}
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  backgroundColor: "#fff7ed",
                                  border: "1px solid #ffedd5",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                  "&:hover": { transform: "translateY(-2px)", boxShadow: "0 4px 12px rgba(234,88,12,0.12)" },
                                }}
                              >
                                <Box>
                                  <Typography variant="caption" fontWeight="600" color="#9a3412" sx={{ fontSize: "0.72rem" }}>
                                    Total Financial Loss
                                  </Typography>
                                  <Typography variant="h6" fontWeight="800" color="#ea580c" sx={{ mt: 0.3 }}>
                                    {formatCurrency(damagedSummary.total_financial_loss || 0)}
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    p: 1,
                                    borderRadius: 1.5,
                                    backgroundColor: "#fff",
                                    color: "#ea580c",
                                    boxShadow: "0 2px 4px rgba(234,88,12,0.08)",
                                  }}
                                >
                                  <TrendingDownIcon />
                                </Box>
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                              <Box
                                onClick={() => navigate("/invoice/damaged-report", { state: getDamagedNavState() })}
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  backgroundColor: "#f0f9ff",
                                  border: "1px solid #e0f2fe",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                  "&:hover": { transform: "translateY(-2px)", boxShadow: "0 4px 12px rgba(2,132,199,0.12)" },
                                }}
                              >
                                <Box>
                                  <Typography variant="caption" fontWeight="600" color="#075985" sx={{ fontSize: "0.72rem" }}>
                                    Damage Incidents
                                  </Typography>
                                  <Typography variant="h6" fontWeight="800" color="#0284c7" sx={{ mt: 0.3 }}>
                                    {damagedSummary.total_incidents || 0}{" "}
                                    <Typography component="span" variant="caption" fontWeight="600" color="#0369a1">
                                      records
                                    </Typography>
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    p: 1,
                                    borderRadius: 1.5,
                                    backgroundColor: "#fff",
                                    color: "#0284c7",
                                    boxShadow: "0 2px 4px rgba(2,132,199,0.08)",
                                  }}
                                >
                                  <ReceiptLongIcon />
                                </Box>
                              </Box>
                            </Grid>
                          </Grid>
                        )}

                        {/* Breakdown Visuals (2 Columns) */}
                        <Grid container spacing={3} mb={3}>
                          {/* Horizontal Mini Bar Chart: Top Damaged Products by Value */}
                          <Grid item xs={12} md={7}>
                            <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #f1f5f9", backgroundColor: "#fafafa", height: "100%" }}>
                              <Typography variant="subtitle2" fontWeight="700" color="#334155" mb={1.5} sx={{ fontSize: "0.82rem" }}>
                                Top Damaged Products by Value (AED)
                              </Typography>
                              {isDamagedLoading ? (
                                <Box py={2}>
                                  <ShinySkeleton height={180} borderRadius={8} />
                                </Box>
                              ) : damagedTopProducts.length === 0 ? (
                                <Box py={4} textAlign="center">
                                  <Typography variant="caption" color="text.secondary">
                                    No damaged product loss recorded for this filter.
                                  </Typography>
                                </Box>
                              ) : (
                                <Chart
                                  options={{
                                    chart: {
                                      type: "bar",
                                      height: 210,
                                      toolbar: { show: false },
                                      events: {
                                        dataPointSelection: (event, chartContext, config) => {
                                          if (config && typeof config.dataPointIndex === "number" && config.dataPointIndex >= 0) {
                                            const p = damagedTopProducts[config.dataPointIndex];
                                            if (p) {
                                              setTimeout(() => {
                                                navigate("/invoice/damaged-report", { state: getDamagedNavState(p.product_name) });
                                              }, 50);
                                            }
                                          }
                                        },
                                      },
                                    },
                                    plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "55%" } },
                                    colors: ["#ef4444"],
                                    dataLabels: { enabled: false },
                                    xaxis: { categories: damagedTopProducts.map((p) => p.product_name) },
                                    grid: { borderColor: "#f1f5f9" },
                                    tooltip: { y: { formatter: (val) => formatCurrency(val) } },
                                  }}
                                  series={[{ name: "Financial Loss (AED)", data: damagedTopProducts.map((p) => p.total_loss) }]}
                                  type="bar"
                                  height={210}
                                />
                              )}
                            </Box>
                          </Grid>

                          {/* Donut Chart: Damaged Value by Store / Location */}
                          <Grid item xs={12} md={5}>
                            <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #f1f5f9", backgroundColor: "#fafafa", height: "100%" }}>
                              <Typography variant="subtitle2" fontWeight="700" color="#334155" mb={1.5} sx={{ fontSize: "0.82rem" }}>
                                Damaged Value by Store / Location
                              </Typography>
                              {isDamagedLoading ? (
                                <Box py={2}>
                                  <ShinySkeleton height={180} borderRadius={8} />
                                </Box>
                              ) : damagedStoreBreakdown.length === 0 ? (
                                <Box py={4} textAlign="center">
                                  <Typography variant="caption" color="text.secondary">
                                    No store damage records found.
                                  </Typography>
                                </Box>
                              ) : (
                                <Chart
                                  options={{
                                    chart: {
                                      type: "donut",
                                      height: 210,
                                      events: {
                                        dataPointSelection: (event, chartContext, config) => {
                                          if (config && typeof config.dataPointIndex === "number" && config.dataPointIndex >= 0) {
                                            const s = damagedStoreBreakdown[config.dataPointIndex];
                                            if (s) {
                                              setTimeout(() => {
                                                navigate("/invoice/damaged-report", { state: getDamagedNavState(null, s.store_id, s.store_name) });
                                              }, 50);
                                            }
                                          }
                                        },
                                      },
                                    },
                                    plotOptions: { pie: { expandOnClick: false } },
                                    labels: damagedStoreBreakdown.map((s) => s.store_name),
                                    colors: ["#dc2626", "#ea580c", "#2563eb", "#8b5cf6", "#10b981"],
                                    legend: { position: "bottom", fontSize: "11px" },
                                    tooltip: { y: { formatter: (val) => formatCurrency(val) } },
                                    dataLabels: { enabled: false },
                                  }}
                                  series={damagedStoreBreakdown.map((s) => s.total_loss)}
                                  type="donut"
                                  height={210}
                                />
                              )}
                            </Box>
                          </Grid>
                        </Grid>

                        {/* Detailed Damage Incidents Table (Shown by default) */}
                        <Box sx={{ borderTop: "1px solid #f1f5f9", pt: 5 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                            <Typography variant="subtitle2" fontWeight="700" color="#1e293b" sx={{ fontSize: "0.85rem" }}>
                              Top 10 Damaged Incidents
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1.5}>
                              <Select
                                size="small"
                                value={damageSort}
                                onChange={(e) => setDamageSort(e.target.value)}
                                sx={{
                                  fontSize: "0.72rem",
                                  height: 28,
                                  backgroundColor: "#fff",
                                  borderRadius: 1.5,
                                  border: "1px solid #e2e8f0",
                                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                                }}
                              >
                                <MenuItem value="high">High</MenuItem>
                                <MenuItem value="low">Low</MenuItem>
                              </Select>
                              <Typography
                                variant="caption"
                                fontWeight="700"
                                color="#2563eb"
                                sx={{ cursor: "pointer", fontSize: "0.72rem", "&:hover": { textDecoration: "underline" } }}
                                onClick={() => navigate("/invoice/damaged-report", { state: getDamagedNavState() })}
                              >
                                View All
                              </Typography>
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              overflowX: "auto",
                              py: 1,
                              width: "100%",
                              boxSizing: "border-box",
                              "&::-webkit-scrollbar": { display: "none", height: 0, width: 0 },
                              msOverflowStyle: "none",
                              scrollbarWidth: "none",
                            }}
                          >
                            <Box sx={{ width: "100%", boxSizing: "border-box" }}>
                              {/* Sub-header row */}
                              <Box display="flex" alignItems="center" pb={1} mb={1} sx={{ borderBottom: "1px solid #f1f5f9", px: 0.5, width: "100%", boxSizing: "border-box" }}>
                                <Box sx={{ width: "12%" }}>
                                  <Typography variant="caption" fontWeight="600" color="#64748b" sx={{ fontSize: "0.70rem", whiteSpace: "nowrap" }}>
                                    Date
                                  </Typography>
                                </Box>
                                <Box sx={{ width: "12%" }}>
                                  <Typography variant="caption" fontWeight="600" color="#64748b" sx={{ fontSize: "0.70rem", whiteSpace: "nowrap" }}>
                                    Ref ID
                                  </Typography>
                                </Box>
                                <Box sx={{ width: "32%", px: 0.5 }}>
                                  <Typography variant="caption" fontWeight="600" color="#64748b" sx={{ fontSize: "0.70rem", whiteSpace: "nowrap" }}>
                                    Product Name
                                  </Typography>
                                </Box>
                                <Box sx={{ width: "24%", px: 0.5 }}>
                                  <Typography variant="caption" fontWeight="600" color="#64748b" sx={{ fontSize: "0.70rem", whiteSpace: "nowrap" }}>
                                    Store / Location
                                  </Typography>
                                </Box>
                                <Box sx={{ width: "8%", textAlign: "center" }}>
                                  <Typography variant="caption" fontWeight="600" color="#64748b" sx={{ fontSize: "0.70rem", whiteSpace: "nowrap" }}>
                                    Damaged Qty
                                  </Typography>
                                </Box>
                                <Box sx={{ width: "12%", textAlign: "right" }}>
                                  <Typography variant="caption" fontWeight="600" color="#64748b" sx={{ fontSize: "0.70rem", whiteSpace: "nowrap" }}>
                                    Total Loss (AED)
                                  </Typography>
                                </Box>
                              </Box>

                              {isDamagedLoading ? (
                                <Box display="flex" flexDirection="column" gap={1.2} py={1}>
                                  {[1, 2, 3, 4].map((i) => (
                                    <ShinySkeleton key={i} height={32} borderRadius={6} />
                                  ))}
                                </Box>
                              ) : sortedDamagedRecords.length === 0 ? (
                                <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                                  <Typography variant="body2" color="text.secondary">
                                    No damage records match current filters.
                                  </Typography>
                                </Box>
                              ) : (
                                <Box display="flex" flexDirection="column" gap={0.8} width="100%" sx={{ boxSizing: "border-box" }}>
                                  {sortedDamagedRecords.map((row, idx) => (
                                    <Box
                                      key={`${row.ref_id || 'item'}-${row.id || idx}-${damageSort}-${idx}`}
                                      display="flex"
                                      alignItems="center"
                                      py={0.4}
                                      onClick={() => navigate("/invoice/damaged-report", { state: getDamagedNavState(row.product_name || row.ref_id, row.store_id) })}
                                      sx={{
                                        cursor: "pointer",
                                        "&:hover": { backgroundColor: "#f8fafc" },
                                        borderRadius: 1.5,
                                        px: 0.5,
                                        width: "100%",
                                        boxSizing: "border-box",
                                        transition: "background-color 0.18s ease",
                                      }}
                                    >
                                      <Box sx={{ width: "12%" }}>
                                        <Typography variant="caption" color="#64748b" sx={{ fontSize: "0.72rem" }}>
                                          {row.record_date}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ width: "12%" }}>
                                        <Typography variant="caption" fontWeight="700" color="#2563eb" sx={{ fontSize: "0.72rem", "&:hover": { textDecoration: "underline" } }}>
                                          {row.ref_id}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ width: "32%", px: 0.5 }}>
                                        <Tooltip title={row.product_name || ""} arrow placement="top">
                                          <Typography variant="body2" fontWeight="600" color="#1e293b" sx={{ fontSize: "0.74rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", "&:hover": { color: "#2563eb" } }}>
                                            {row.product_name}
                                          </Typography>
                                        </Tooltip>
                                      </Box>
                                      <Box sx={{ width: "24%", px: 0.5 }}>
                                        <Tooltip title={row.store_name || ""} arrow placement="top">
                                          <Typography variant="body2" fontWeight="600" color="#475569" sx={{ fontSize: "0.74rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {row.store_name}
                                          </Typography>
                                        </Tooltip>
                                      </Box>
                                      <Box sx={{ width: "8%", textAlign: "center" }}>
                                        <Box
                                          sx={{
                                            backgroundColor: "#fee2e2",
                                            color: "#dc2626",
                                            fontWeight: "700",
                                            fontSize: "0.68rem",
                                            px: 0.8,
                                            py: 0.2,
                                            borderRadius: 3,
                                            display: "inline-block",
                                          }}
                                        >
                                          {formatNumber(row.damaged_qty)}
                                        </Box>
                                      </Box>
                                      <Box sx={{ width: "12%", textAlign: "right" }}>
                                        <Typography variant="body2" fontWeight="800" color="#991b1b" sx={{ fontSize: "0.74rem", whiteSpace: "nowrap" }}>
                                          {formatCurrency(row.total_loss)}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </>
                    );
                  })()}
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Floating Circular Filter Button & Horizontal Slide-Out Filter Bar */}
        {(() => {
          const isFilterActive = storeId !== 0 || period !== "monthly" || customerId !== 0 || salesPersonId !== 0 || !!fromDate || !!toDate;

          return (
            <Box
              sx={{
                position: "fixed",
                bottom: 24,
                right: 24,
                zIndex: 9999,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                transform: "translateY(0) scale(1)",
                opacity: 1,
                pointerEvents: "auto",
                transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
              }}
            >
              {/* Horizontal Filter Bar - Slides out Right to Left */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.2,
                  py: 1,
                  px: 2,
                  borderRadius: 3,
                  backgroundColor: "#fff",
                  border: "1px solid #cbd5e1",
                  boxShadow: "0 12px 36px -4px rgba(15, 23, 42, 0.16)",
                  maxWidth: filterExpanded ? "92vw" : 0,
                  opacity: filterExpanded ? 1 : 0,
                  overflow: filterExpanded ? "visible" : "hidden",
                  whiteSpace: "nowrap",
                  transition: "all 0.45s cubic-bezier(0.34, 1.56, 0.64, 1)",
                  mr: filterExpanded ? 1.5 : 0,
                  pointerEvents: filterExpanded ? "auto" : "none",
                }}
              >
                {/* 1. Period Autocomplete */}
                <Autocomplete
                  size="small"
                  options={[
                    { value: "all", label: "All Time" },
                    { value: "daily", label: "Today" },
                    { value: "weekly", label: "Weekly" },
                    { value: "monthly", label: "This Month" },
                    { value: "yearly", label: "Yearly" },
                  ]}
                  getOptionLabel={(option) => option.label || ""}
                  slotProps={{ popper: { style: { zIndex: 12000 } } }}
                  value={
                    [
                      { value: "all", label: "All Time" },
                      { value: "daily", label: "Today" },
                      { value: "weekly", label: "Weekly" },
                      { value: "monthly", label: "This Month" },
                      { value: "yearly", label: "Yearly" },
                    ].find((p) => p.value === period) || { value: "monthly", label: "This Month" }
                  }
                  onChange={(event, newValue) => {
                    if (newValue) {
                      handlePeriodChange(newValue.value);
                    }
                  }}
                  isOptionEqualToValue={(option, val) => option.value === val.value}
                  disableClearable
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Period"
                      sx={{
                        width: 140,
                        backgroundColor: "#fff",
                        borderRadius: 1.5,
                        "& .MuiOutlinedInput-root": {
                          height: 36,
                          borderRadius: 1.5,
                          border: "1px solid #cbd5e1",
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          "& fieldset": { border: "none" },
                        },
                      }}
                    />
                  )}
                />

                {/* 2. Store Autocomplete */}
                <Autocomplete
                  size="small"
                  options={[{ id: 0, store_name: "All Stores" }, ...stores]}
                  getOptionLabel={(option) => option.store_name || ""}
                  slotProps={{ popper: { style: { zIndex: 12000 } } }}
                  value={
                    [{ id: 0, store_name: "All Stores" }, ...stores].find(
                      (s) => Number(s.id) === Number(storeId)
                    ) || { id: 0, store_name: "All Stores" }
                  }
                  onChange={(event, newValue) => {
                    setStoreId(newValue ? newValue.id : 0);
                  }}
                  isOptionEqualToValue={(option, val) => Number(option.id) === Number(val.id)}
                  disableClearable
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Store"
                      sx={{
                        width: 170,
                        backgroundColor: "#fff",
                        borderRadius: 1.5,
                        "& .MuiOutlinedInput-root": {
                          height: 36,
                          borderRadius: 1.5,
                          border: "1px solid #cbd5e1",
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          "& fieldset": { border: "none" },
                        },
                      }}
                    />
                  )}
                />

                {/* 3. Customer Autocomplete */}
                <Autocomplete
                  size="small"
                  options={[{ customer_id: 0, name: "All Customers" }, ...customersList]}
                  getOptionLabel={(option) => option.name || ""}
                  slotProps={{ popper: { style: { zIndex: 12000 } } }}
                  value={
                    [{ customer_id: 0, name: "All Customers" }, ...customersList].find(
                      (c) => Number(c.customer_id) === Number(customerId)
                    ) || { customer_id: 0, name: "All Customers" }
                  }
                  onChange={(event, newValue) => {
                    setCustomerId(newValue ? newValue.customer_id : 0);
                  }}
                  isOptionEqualToValue={(option, val) => Number(option.customer_id) === Number(val.customer_id)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Customer"
                      sx={{
                        width: 170,
                        backgroundColor: "#fff",
                        borderRadius: 1.5,
                        "& .MuiOutlinedInput-root": {
                          height: 36,
                          borderRadius: 1.5,
                          border: "1px solid #cbd5e1",
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          "& fieldset": { border: "none" },
                        },
                      }}
                    />
                  )}
                />

                {/* 4. Salesperson Autocomplete */}
                <Autocomplete
                  size="small"
                  options={[{ id: 0, name: "All Salespersons" }, ...salesPersonsList]}
                  getOptionLabel={(option) => option.name || ""}
                  slotProps={{ popper: { style: { zIndex: 12000 } } }}
                  value={
                    [{ id: 0, name: "All Salespersons" }, ...salesPersonsList].find(
                      (sp) => Number(sp.id) === Number(salesPersonId)
                    ) || { id: 0, name: "All Salespersons" }
                  }
                  onChange={(event, newValue) => {
                    setSalesPersonId(newValue ? newValue.id : 0);
                  }}
                  isOptionEqualToValue={(option, val) => Number(option.id) === Number(val.id)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Salesperson"
                      sx={{
                        width: 170,
                        backgroundColor: "#fff",
                        borderRadius: 1.5,
                        "& .MuiOutlinedInput-root": {
                          height: 36,
                          borderRadius: 1.5,
                          border: "1px solid #cbd5e1",
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          "& fieldset": { border: "none" },
                        },
                      }}
                    />
                  )}
                />

                {/* 5. From Date */}
                <Box
                  display="flex"
                  alignItems="center"
                  sx={{
                    height: 36,
                    backgroundColor: "#fff",
                    borderRadius: 1.5,
                    border: "1px solid #cbd5e1",
                    px: 1,
                  }}
                >
                  <Typography variant="caption" fontWeight="700" color="#64748b" sx={{ fontSize: "0.74rem", mr: 0.6, flexShrink: 0 }}>
                    From:
                  </Typography>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    style={{
                      border: "none",
                      outline: "none",
                      backgroundColor: "transparent",
                      fontSize: "0.76rem",
                      fontWeight: 600,
                      color: "#1e293b",
                      fontFamily: "inherit",
                      cursor: "pointer",
                    }}
                  />
                </Box>

                {/* 6. To Date */}
                <Box
                  display="flex"
                  alignItems="center"
                  sx={{
                    height: 36,
                    backgroundColor: "#fff",
                    borderRadius: 1.5,
                    border: "1px solid #cbd5e1",
                    px: 1,
                  }}
                >
                  <Typography variant="caption" fontWeight="700" color="#64748b" sx={{ fontSize: "0.74rem", mr: 0.6, flexShrink: 0 }}>
                    To:
                  </Typography>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    style={{
                      border: "none",
                      outline: "none",
                      backgroundColor: "transparent",
                      fontSize: "0.76rem",
                      fontWeight: 600,
                      color: "#1e293b",
                      fontFamily: "inherit",
                      cursor: "pointer",
                    }}
                  />
                </Box>

                {/* Reset Filters */}
                <Tooltip title="Reset Filters" arrow>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setStoreId(0);
                      setCustomerId(0);
                      setSalesPersonId(0);
                      handlePeriodChange("monthly");
                      setSearchQuery("");
                    }}
                    sx={{ color: "#64748b", "&:hover": { color: "#dc2626", backgroundColor: "#fef2f2" } }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Collapse Button */}
                <Tooltip title="Collapse Filters" arrow>
                  <IconButton
                    size="small"
                    onClick={() => setFilterExpanded(false)}
                    sx={{ color: "#64748b", "&:hover": { color: "#0f172a", backgroundColor: "#f1f5f9" } }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Circular Floating Filter Button */}
              <Tooltip title={filterExpanded ? "Close Filters" : "Quick Filters"} arrow placement="left">
                <IconButton
                  onClick={() => setFilterExpanded((prev) => !prev)}
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: "50%",
                    backgroundColor: filterExpanded ? "#2563eb" : "#fff",
                    color: filterExpanded ? "#fff" : "#2563eb",
                    border: "1px solid #cbd5e1",
                    boxShadow: isFilterActive
                      ? "0 8px 25px rgba(37, 99, 235, 0.35)"
                      : "0 8px 24px rgba(15, 23, 42, 0.12)",
                    transition: "all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)",
                    "&:hover": {
                      transform: "scale(1.08)",
                      backgroundColor: filterExpanded ? "#1d4ed8" : "#eff6ff",
                    },
                  }}
                >
                  {filterExpanded ? (
                    <ClearIcon sx={{ fontSize: 22 }} />
                  ) : (
                    <Box display="flex" alignItems="center" justifyContent="center" position="relative">
                      <FilterAltIcon sx={{ fontSize: 22 }} />
                      {isFilterActive && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: -2,
                            right: -2,
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: "#2563eb",
                            boxShadow: "0 0 6px #2563eb",
                          }}
                        />
                      )}
                    </Box>
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          );
        })()}
      </Box>
    </Layout>
  );
}

export default SalesExecDashboard;
